import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type {
  WorkflowBlockDefinition,
  WorkflowBlockField,
  WorkflowBuilderPageData,
  WorkflowGraph,
  WorkflowGraphNode,
  WorkflowValidationIssue,
  WorkflowValidationResult,
  WorkflowData,
  WorkflowLeadSearchResult,
  WorkflowActionRunDetailData,
  WorkflowScheduleRunDetailData,
  WorkflowScheduleRunData,
} from "@/types/leadflow"
import { createWorkflowEditor, type WorkflowEditorHandle, type WorkflowEditorSelection } from "@/workflows/editor"
import { blockByType } from "@/workflows/blocks/registry"
import { buildWorkflowExport, downloadWorkflowExport, sanitizeWorkflowFilename } from "@/workflows/export"
import { runtimeEndpointNotFoundMessage, workflowRuntimeResponseMessage, workflowRuntimeUrl, type WorkflowRuntimeMode } from "@/workflows/runtimeRequests"
import { DURATION_UNIT_OPTIONS, conditionValueControlFor, durationDisplayFromMinutes, minutesFromDuration, normalizeConditionConfig, operatorRequiresValue, operatorsForVariable, workflowOperatorByKey, workflowVariableByKey } from "@/workflows/variables/registry"
import { blankWorkflowGraph, cloneWorkflowGraph, firstGraphDifference, graphHasDraftChanges, isEmptyWorkflowGraph, workflowGraphDigest } from "@/workflows/graph"
import { Bell, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Download, KanbanSquare, Plus, Shapes, Trash2, Workflow as WorkflowIcon, Zap } from "lucide-react"

type EditorStatus = "idle" | "saving" | "saved" | "error"
type BlockCategory = "all" | "trigger" | "logic" | "integration" | "action"
type ToastTone = "success" | "error" | "info"
type WorkflowToast = { id: number; tone: ToastTone; message: string }

type Props = WorkflowBuilderPageData

const STORAGE_KEY = "workflow-builder-sidebar-open"
const CATEGORY_ORDER: BlockCategory[] = ["trigger", "logic", "integration", "action"]

function blockToneClass(category: string) {
  switch (category) {
    case "trigger": return "workflow-tone--trigger"
    case "logic": return "workflow-tone--logic"
    case "action": return "workflow-tone--action"
    default: return "workflow-tone--trigger"
  }
}

export default function WorkflowsBuilder({ workflows, workflow, workflow_graph, block_definitions, validation_result, workflow_errors, flash_notice, trigger_types, stage_options, users, workflow_icon_options }: Props) {
  const editorContainerRef = React.useRef<HTMLDivElement | null>(null)
  const editorRef = React.useRef<WorkflowEditorHandle | null>(null)
  const mountedRef = React.useRef(false)
  const latestGraphRef = React.useRef<WorkflowGraph>(cloneWorkflowGraph(workflow_graph?.graph_json ?? blankWorkflowGraph()))
  const serverGraphRef = React.useRef<WorkflowGraph>(cloneWorkflowGraph(workflow_graph?.graph_json ?? blankWorkflowGraph()))
  const applyingSavedGraphRef = React.useRef<WorkflowGraph | null>(null)
  const savingOrPublishingRef = React.useRef(false)
  const savedGraphBaselineRef = React.useRef<WorkflowGraph>(cloneWorkflowGraph(workflow_graph?.graph_json ?? blankWorkflowGraph()))
  const [sidebarOpen, setSidebarOpen] = React.useState(() => readStoredBoolean(STORAGE_KEY, true))
  const [blockPickerOpen, setBlockPickerOpen] = React.useState(false)
  const [blockSearch, setBlockSearch] = React.useState("")
  const [blockCategory, setBlockCategory] = React.useState<BlockCategory>("all")
  const [selection, setSelection] = React.useState<WorkflowEditorSelection>(null)
  const [graph, setGraph] = React.useState<WorkflowGraph>(() => cloneWorkflowGraph(workflow_graph?.graph_json ?? blankWorkflowGraph()))
  const [, setSavedGraphBaseline] = React.useState<WorkflowGraph>(() => cloneWorkflowGraph(workflow_graph?.graph_json ?? blankWorkflowGraph()))
  const [validation, setValidation] = React.useState<WorkflowValidationResult | null>(validation_result)
  const [editorReady, setEditorReady] = React.useState(false)
  const [editorError, setEditorError] = React.useState<string | null>(null)
  const [inspectorOpen, setInspectorOpen] = React.useState(true)
  const [deleteWorkflowOpen, setDeleteWorkflowOpen] = React.useState(false)
  const [deleteWorkflowConfirmation, setDeleteWorkflowConfirmation] = React.useState("")
  const [createWorkflowOpen, setCreateWorkflowOpen] = React.useState(false)
  const [createWorkflowName, setCreateWorkflowName] = React.useState("Untitled workflow")
  const [createWorkflowDescription, setCreateWorkflowDescription] = React.useState("")
  const [createWorkflowIcon, setCreateWorkflowIcon] = React.useState("workflow")
  const [status, setStatus] = React.useState<EditorStatus>("idle")
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null)
  const [savingGraph, setSavingGraph] = React.useState(false)
  const [validatingGraph, setValidatingGraph] = React.useState(false)
  const [publishingGraph, setPublishingGraph] = React.useState(false)
  const [runningGraphMode, setRunningGraphMode] = React.useState<WorkflowRuntimeMode | null>(null)
  const [leadPickerMode, setLeadPickerMode] = React.useState<WorkflowRuntimeMode | null>(null)
  const [leadSearchQuery, setLeadSearchQuery] = React.useState("")
  const [leadSearchResults, setLeadSearchResults] = React.useState<WorkflowLeadSearchResult[]>([])
  const [leadSearchMessage, setLeadSearchMessage] = React.useState<string | null>(null)
  const [internalTestLead, setInternalTestLead] = React.useState<WorkflowLeadSearchResult | null>(null)
  const [internalTestGraph, setInternalTestGraph] = React.useState<WorkflowGraph | null>(null)
  const [schedulePreview, setSchedulePreview] = React.useState<Record<string, unknown> | null>(null)
  const [schedulePreviewStaleReason, setSchedulePreviewStaleReason] = React.useState<string | null>(null)
  const [schedulePreviewError, setSchedulePreviewError] = React.useState<string | null>(null)
  const [scheduleRunError, setScheduleRunError] = React.useState<string | null>(null)
  const [scheduleRunResult, setScheduleRunResult] = React.useState<Record<string, unknown> | null>(null)
  const [runningScheduleAction, setRunningScheduleAction] = React.useState<"preview" | "run" | null>(null)
  const [workflowOverride, setWorkflowOverride] = React.useState<WorkflowData | null>(null)
  const [automationDetailsOpen, setAutomationDetailsOpen] = React.useState(false)
  const [previewModalOpen, setPreviewModalOpen] = React.useState(false)
  const [historyModalOpen, setHistoryModalOpen] = React.useState(false)
  const [systemReadinessOpen, setSystemReadinessOpen] = React.useState(false)
  const [systemReadiness, setSystemReadiness] = React.useState<Record<string, unknown> | null>(null)
  const [systemReadinessLoading, setSystemReadinessLoading] = React.useState(false)
  const [fieldRoutesReconciliationOpen, setFieldRoutesReconciliationOpen] = React.useState(false)
  const [fieldRoutesReconciliation, setFieldRoutesReconciliation] = React.useState<Record<string, unknown> | null>(null)
  const [fieldRoutesReconciliationLoading, setFieldRoutesReconciliationLoading] = React.useState(false)
  const [fieldRoutesReconciliationQuery, setFieldRoutesReconciliationQuery] = React.useState("")
  const [fieldRoutesReconciliationMode, setFieldRoutesReconciliationMode] = React.useState<"local" | "live">("local")
  const [fieldRoutesCustomerId, setFieldRoutesCustomerId] = React.useState("")
  const [fieldRoutesSubscriptionId, setFieldRoutesSubscriptionId] = React.useState("")
  const [fieldRoutesLeadSyncOpen, setFieldRoutesLeadSyncOpen] = React.useState(false)
  const [fieldRoutesLeadSync, setFieldRoutesLeadSync] = React.useState<Record<string, unknown> | null>(null)
  const [fieldRoutesLeadSyncLoading, setFieldRoutesLeadSyncLoading] = React.useState(false)
  const [fieldRoutesLeadSyncSubscriptionId, setFieldRoutesLeadSyncSubscriptionId] = React.useState("")
  const [fieldRoutesLeadSyncLimit, setFieldRoutesLeadSyncLimit] = React.useState("25")
  const [fieldRoutesLifecycleOpen, setFieldRoutesLifecycleOpen] = React.useState(false)
  const [fieldRoutesLifecycle, setFieldRoutesLifecycle] = React.useState<Record<string, unknown> | null>(null)
  const [fieldRoutesLifecycleLoading, setFieldRoutesLifecycleLoading] = React.useState(false)
  const [fieldRoutesLifecycleLeadId, setFieldRoutesLifecycleLeadId] = React.useState("")
  const [fieldRoutesLifecycleSubscriptionId, setFieldRoutesLifecycleSubscriptionId] = React.useState("")
  const [fieldRoutesLifecycleLimit, setFieldRoutesLifecycleLimit] = React.useState("25")
  const [emailTemplateCatalog, setEmailTemplateCatalog] = React.useState<Record<string, unknown> | null>(null)
  const [emailTemplateCatalogLoading, setEmailTemplateCatalogLoading] = React.useState(false)
  const [waitingRunsOpen, setWaitingRunsOpen] = React.useState(false)
  const [waitingRuns, setWaitingRuns] = React.useState<Record<string, unknown>[]>([])
  const [waitingRunsLoading, setWaitingRunsLoading] = React.useState(false)
  const [waitingRunActionId, setWaitingRunActionId] = React.useState<string | null>(null)
  const [validationModalOpen, setValidationModalOpen] = React.useState(false)
  const [runConfirmOpen, setRunConfirmOpen] = React.useState(false)
  const [runResultsOpen, setRunResultsOpen] = React.useState(false)
  const [remainingPreviewLoading, setRemainingPreviewLoading] = React.useState(false)
  const [toasts, setToasts] = React.useState<WorkflowToast[]>([])
  const toastIdRef = React.useRef(1)
  const schedulePreviewRef = React.useRef<Record<string, unknown> | null>(null)

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined
    const html = document.documentElement
    const body = document.body
    const previousHtmlOverflow = html.style.overflow
    const previousBodyOverflow = body.style.overflow
    const previousBodyOverscroll = body.style.overscrollBehavior
    html.style.overflow = "hidden"
    body.style.overflow = "hidden"
    body.style.overscrollBehavior = "none"
    return () => {
      html.style.overflow = previousHtmlOverflow
      body.style.overflow = previousBodyOverflow
      body.style.overscrollBehavior = previousBodyOverscroll
    }
  }, [])

  const activeWorkflow = workflowOverride ?? workflow ?? null
  const hasEditor = Boolean(activeWorkflow && workflow_graph)
  const blockList = React.useMemo(() => block_definitions, [block_definitions])
  const blockMap = React.useMemo(() => new Map(blockList.map((block) => [block.type, block])), [blockList])
  const filteredBlocks = React.useMemo(() => groupBlocks(blockList, blockSearch, blockCategory), [blockList, blockSearch, blockCategory])
  const selectedNode = selection?.kind === "node" ? graph.nodes.find((node) => node.id === selection.id) ?? null : null
  const selectedBlock = selectedNode ? blockMap.get(selectedNode.type) ?? blockByType(selectedNode.type) ?? null : null
  const graphLoaded = hasEditor ? graph : blankWorkflowGraph()
  const draftChanged = Boolean(activeWorkflow && workflow_graph && graphHasDraftChanges(graphLoaded, savedGraphBaselineRef.current))
  const publishedChanged = Boolean(activeWorkflow && workflow_graph && graphHasDraftChanges(graphLoaded, workflow_graph.published_graph_json ?? null))
  const workflowTone = activeWorkflow ? (activeWorkflow.status === "published" ? "accent" : activeWorkflow.status === "inactive" ? "signal" : "muted") : "muted"

  const pushToast = React.useCallback((message: string, tone: ToastTone = "info") => {
    if (!message) return
    const id = toastIdRef.current++
    setToasts((items) => items.some((item) => item.message === message && item.tone === tone) ? items : [...items, { id, tone, message }])
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        setToasts((items) => items.filter((item) => item.id !== id))
      }, tone === "error" ? 8000 : 4500)
    }
  }, [])


  const logGraphDifference = React.useCallback((label: string, baseline: WorkflowGraph, live: WorkflowGraph) => {
    if (!import.meta.env.DEV) return
    const difference = firstGraphDifference(baseline, live)
    console.debug("Workflow graph comparison", {
      label,
      baselineDigest: workflowGraphDigest(baseline),
      liveDigest: workflowGraphDigest(live),
      difference,
    })
  }, [])

  const setSavedBaselineGraph = React.useCallback((nextGraph: WorkflowGraph) => {
    const cloned = cloneWorkflowGraph(nextGraph)
    savedGraphBaselineRef.current = cloned
    setSavedGraphBaseline(cloned)
    serverGraphRef.current = cloned
  }, [])

  const canonicalBuilderUrl = React.useCallback((workflowId: number) => `/workflows/${workflowId}/builder`, [])

  const ensureCanonicalBuilderUrl = React.useCallback((workflowId: number) => {
    if (typeof window === "undefined") return
    const canonical = canonicalBuilderUrl(workflowId)
    if (window.location.pathname !== canonical) {
      window.history.replaceState(window.history.state, "", `${canonical}${window.location.search}${window.location.hash}`)
    }
  }, [canonicalBuilderUrl])

  const flushEditorLoadEmit = React.useCallback(async () => {
    await Promise.resolve()
    await Promise.resolve()
  }, [])

  const applyServerConfirmedGraph = React.useCallback(async (savedGraph: WorkflowGraph, label: string) => {
    const baseline = cloneWorkflowGraph(savedGraph)
    savingOrPublishingRef.current = true
    applyingSavedGraphRef.current = baseline
    setSavedBaselineGraph(baseline)
    latestGraphRef.current = baseline
    setGraph(baseline)

    if (editorRef.current) {
      await editorRef.current.load(baseline)
      await flushEditorLoadEmit()
      const reconciled = normalizeGraphForEditor(editorRef.current.serialize(), stage_options)
      if (graphHasDraftChanges(reconciled, baseline)) {
        logGraphDifference(`${label}: post-load editor graph differs`, baseline, reconciled)
      } else {
        setSavedBaselineGraph(reconciled)
        latestGraphRef.current = reconciled
        setGraph(reconciled)
      }
    }

    await flushEditorLoadEmit()
    applyingSavedGraphRef.current = null
    savingOrPublishingRef.current = false
  }, [flushEditorLoadEmit, logGraphDifference, setSavedBaselineGraph, stage_options])

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      editorRef.current?.destroy()
      editorRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, String(sidebarOpen))
  }, [sidebarOpen])

  React.useEffect(() => {
    schedulePreviewRef.current = schedulePreview
  }, [schedulePreview])

  React.useEffect(() => {
    if (!blockPickerOpen) return
    setBlockSearch("")
    setBlockCategory("all")
  }, [blockPickerOpen])

  React.useEffect(() => {
    setWorkflowOverride(null)
  }, [workflow?.id])

  React.useEffect(() => {
    if (activeWorkflow) ensureCanonicalBuilderUrl(activeWorkflow.id)
  }, [activeWorkflow?.id, ensureCanonicalBuilderUrl])

  React.useEffect(() => {
    if (applyingSavedGraphRef.current) return
    const nextGraph = cloneWorkflowGraph(workflow_graph?.graph_json ?? blankWorkflowGraph())
    setSavedBaselineGraph(nextGraph)
    setGraph(nextGraph)
    setValidation(validation_result)
    setSelection(null)
    latestGraphRef.current = nextGraph
  }, [workflow_graph?.graph_json, workflow_graph?.version, workflow_graph?.published_version, workflow_graph?.status, validation_result, setSavedBaselineGraph])

  React.useEffect(() => {
    if (!hasEditor || !editorContainerRef.current) {
      editorRef.current?.destroy()
      editorRef.current = null
      setEditorReady(false)
      setEditorError(null)
      return
    }

    let cancelled = false
    editorRef.current?.destroy()
    editorRef.current = null
    setEditorReady(false)
    setEditorError(null)

    createWorkflowEditor(editorContainerRef.current, {
      onChange: (nextGraph) => {
        latestGraphRef.current = nextGraph
        setGraph(nextGraph)
        const applyingSavedGraph = applyingSavedGraphRef.current
        if (applyingSavedGraph) {
          if (!graphHasDraftChanges(nextGraph, applyingSavedGraph)) {
            setSavedBaselineGraph(nextGraph)
            return
          }
          logGraphDifference("first_editor_emit_after_load", applyingSavedGraph, nextGraph)
          applyingSavedGraphRef.current = null
          savingOrPublishingRef.current = false
        }
        setStatus("idle")
        setStatusMessage(null)

        if (schedulePreviewRef.current) {
          setSchedulePreviewStaleReason("This preview is no longer current because the workflow changed. Preview eligible leads again.")
        }
      },
      onSelectionChange: setSelection,
    }, cloneWorkflowGraph(workflow_graph!.graph_json))
      .then((handle) => {
        if (cancelled || !mountedRef.current) {
          handle.destroy()
          return
        }
        editorRef.current = handle
        setEditorReady(true)
        latestGraphRef.current = cloneWorkflowGraph(workflow_graph!.graph_json)
        setGraph(cloneWorkflowGraph(workflow_graph!.graph_json))
      })
      .catch((error: unknown) => {
        if (cancelled || !mountedRef.current) return
        setEditorReady(false)
        setEditorError(error instanceof Error ? error.message : "Failed to initialize the workflow builder.")
      })

    return () => {
      cancelled = true
    }
  }, [hasEditor, workflow?.id])

  React.useEffect(() => {
    if (!editorReady || !editorRef.current || !workflow_graph || applyingSavedGraphRef.current) return
    const nextGraph = cloneWorkflowGraph(workflow_graph.graph_json)
    if (!graphHasDraftChanges(nextGraph, latestGraphRef.current)) {
      setValidation(validation_result)
      return
    }
    latestGraphRef.current = nextGraph
    setGraph(nextGraph)
    setValidation(validation_result)
    void editorRef.current.load(nextGraph)
  }, [editorReady, workflow_graph?.graph_json, workflow_graph?.version, workflow_graph?.published_version, workflow_graph?.status, validation_result])

  const saveDraft = () => {
    if (!activeWorkflow || !editorRef.current) return
    const nextGraph = normalizeGraphForEditor(editorRef.current.serialize(), stage_options)
    setSavingGraph(true)
    setStatus("saving")
    setStatusMessage("Saving draft...")
    router.patch(`/workflows/${activeWorkflow.id}/graph`, { graph_json: JSON.stringify(nextGraph) }, {
      preserveScroll: true,
      preserveUrl: true,
      replace: true,
      onSuccess: (page) => {
        const props = page.props as { workflow_graph?: { graph_json?: WorkflowGraph } }
        const savedGraph = cloneWorkflowGraph(props.workflow_graph?.graph_json ?? nextGraph)
        ensureCanonicalBuilderUrl(activeWorkflow.id)
        void applyServerConfirmedGraph(savedGraph, "save").then(() => {
          ensureCanonicalBuilderUrl(activeWorkflow.id)
          setStatus("saved")
          setStatusMessage("Draft saved.")
          pushToast("Draft saved.", "success")
        }).finally(() => setSavingGraph(false))
      },
      onError: () => {
        applyingSavedGraphRef.current = null
        savingOrPublishingRef.current = false
        setSavingGraph(false)
        setStatus("error")
        setStatusMessage("Save failed. Your unsaved changes remain in this browser tab until you leave the page.")
        pushToast("Save failed. Your unsaved changes remain in this browser tab until you leave the page.", "error")
      },
      onFinish: () => {},
    })
  }

  const loadWaitingRuns = async () => {
    if (!activeWorkflow) return
    setWaitingRunsLoading(true)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/waiting_runs`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as { runs?: Record<string, unknown>[]; error?: string; message?: string }
      if (!response.ok) throw new Error(String(data.message || data.error || "Waiting runs failed to load."))
      setWaitingRuns(Array.isArray(data.runs) ? data.runs : [])
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "Waiting runs failed to load.", "error")
    } finally {
      setWaitingRunsLoading(false)
    }
  }

  const openWaitingRuns = async () => {
    if (!activeWorkflow) return
    setWaitingRunsOpen(true)
    await loadWaitingRuns()
  }

  const cancelWaitingRun = async (run: Record<string, unknown>) => {
    if (!activeWorkflow) return
    const runId = String(run.id)
    const workflowName = String(run.workflow_name ?? "workflow")
    const lead = run.lead as Record<string, unknown> | null | undefined
    const leadName = lead ? String(lead.customer_name ?? `Lead #${String(lead.id ?? "")}`) : "Lead unavailable"
    if (!window.confirm(`Cancel this waiting run?\n\nWorkflow: ${workflowName}\nLead: ${leadName}\nWaiting node: ${String(run.node_type ?? "wait")} / ${String(run.node_id ?? "—")}\n\nDownstream actions will not run. Actions completed before the wait will not be reversed.`)) return
    setWaitingRunActionId(`cancel:${runId}`)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/waiting_runs/${runId}/cancel`, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      })
      const data = await response.json() as { message?: string; error?: string }
      if (!response.ok) throw new Error(String(data.message || data.error || "Waiting run could not be cancelled."))
      pushToast(data.message || "Waiting run cancelled.", "success")
      await loadWaitingRuns()
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "Waiting run could not be cancelled.", "error")
    } finally {
      setWaitingRunActionId(null)
    }
  }

  const resumeWaitingRun = async (run: Record<string, unknown>) => {
    if (!activeWorkflow) return
    const runId = String(run.id)
    const early = Boolean(run.early_resume_required)
    if (early && !window.confirm("This wait is not due yet. Resume early?")) return
    if (!early && !window.confirm("Resume this waiting workflow run now?")) return
    setWaitingRunActionId(`resume:${runId}`)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/waiting_runs/${runId}/resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
        body: JSON.stringify({ confirm_early: early }),
      })
      const data = await response.json() as { message?: string; error?: string }
      if (!response.ok) throw new Error(String(data.message || data.error || "Waiting run could not be resumed."))
      pushToast(data.message || "Waiting run resumed.", "success")
      await loadWaitingRuns()
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "Waiting run could not be resumed.", "error")
    } finally {
      setWaitingRunActionId(null)
    }
  }


  const loadEmailTemplateCatalog = React.useCallback(async () => {
    if (!activeWorkflow) return
    setEmailTemplateCatalogLoading(true)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/email_template_catalog`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as Record<string, unknown>
      if (!response.ok) throw new Error(String(data.error_message || data.error || "Email template catalog could not be loaded."))
      setEmailTemplateCatalog(data)
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "Email template catalog could not be loaded.", "error")
    } finally {
      setEmailTemplateCatalogLoading(false)
    }
  }, [activeWorkflow?.id, pushToast])

  const refreshEmailTemplateCatalog = React.useCallback(async () => {
    if (!activeWorkflow) return
    setEmailTemplateCatalogLoading(true)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/refresh_email_template_catalog`, { method: "POST", headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
      const data = await response.json() as Record<string, unknown>
      if (!response.ok) throw new Error(String(data.error_message || data.error || "Email template refresh failed."))
      setEmailTemplateCatalog(data)
      pushToast("Email templates refreshed.", "success")
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "Email template refresh failed.", "error")
    } finally {
      setEmailTemplateCatalogLoading(false)
    }
  }, [activeWorkflow?.id, pushToast])

  React.useEffect(() => {
    if (selectedBlock?.type === "email.send" && !emailTemplateCatalog && !emailTemplateCatalogLoading) {
      void loadEmailTemplateCatalog()
    }
  }, [selectedBlock?.type, emailTemplateCatalog, emailTemplateCatalogLoading, loadEmailTemplateCatalog])


  const loadFieldRoutesReconciliation = React.useCallback(async (page = 1) => {
    if (!activeWorkflow) return
    setFieldRoutesReconciliationLoading(true)
    try {
      const params = new URLSearchParams()
      params.set("page", String(page))
      params.set("per_page", "25")
      params.set("mode", fieldRoutesReconciliationMode)
      if (fieldRoutesReconciliationMode === "live") params.set("limit", "25")
      if (fieldRoutesReconciliationQuery.trim()) params.set("query", fieldRoutesReconciliationQuery.trim())
      if (fieldRoutesCustomerId.trim()) params.set("fieldroutes_customer_id", fieldRoutesCustomerId.trim())
      if (fieldRoutesSubscriptionId.trim()) params.set("fieldroutes_subscription_id", fieldRoutesSubscriptionId.trim())
      const response = await fetch(`/workflows/${activeWorkflow.id}/fieldroutes_reconciliation?${params.toString()}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as Record<string, unknown>
      if (!response.ok) throw new Error(String(data.error_message || data.error || "FieldRoutes reconciliation preview failed."))
      setFieldRoutesReconciliation(data)
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "FieldRoutes reconciliation preview failed.", "error")
    } finally {
      setFieldRoutesReconciliationLoading(false)
    }
  }, [activeWorkflow?.id, fieldRoutesReconciliationMode, fieldRoutesReconciliationQuery, fieldRoutesCustomerId, fieldRoutesSubscriptionId, pushToast])

  const openFieldRoutesReconciliation = async () => {
    if (!activeWorkflow) return
    setFieldRoutesReconciliationOpen(true)
    await loadFieldRoutesReconciliation(1)
  }

  const loadFieldRoutesLeadSyncPreview = React.useCallback(async () => {
    if (!activeWorkflow) return
    setFieldRoutesLeadSyncLoading(true)
    try {
      const params = new URLSearchParams()
      if (fieldRoutesLeadSyncSubscriptionId.trim()) params.set("fieldroutes_subscription_id", fieldRoutesLeadSyncSubscriptionId.trim())
      if (fieldRoutesLeadSyncLimit.trim()) params.set("limit", fieldRoutesLeadSyncLimit.trim())
      const response = await fetch(`/workflows/${activeWorkflow.id}/fieldroutes_lead_sync_preview?${params.toString()}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as Record<string, unknown>
      if (!response.ok) throw new Error(String(data.error_message || data.error || "FieldRoutes lead sync preview failed."))
      setFieldRoutesLeadSync(data)
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "FieldRoutes lead sync preview failed.", "error")
    } finally {
      setFieldRoutesLeadSyncLoading(false)
    }
  }, [activeWorkflow?.id, fieldRoutesLeadSyncLimit, fieldRoutesLeadSyncSubscriptionId, pushToast])

  const openFieldRoutesLeadSyncPreview = async () => {
    if (!activeWorkflow) return
    setFieldRoutesLeadSyncOpen(true)
    await loadFieldRoutesLeadSyncPreview()
  }

  const loadFieldRoutesLifecyclePreview = React.useCallback(async () => {
    if (!activeWorkflow) return
    setFieldRoutesLifecycleLoading(true)
    try {
      const params = new URLSearchParams()
      if (fieldRoutesLifecycleLeadId.trim()) params.set("lead_id", fieldRoutesLifecycleLeadId.trim())
      if (fieldRoutesLifecycleSubscriptionId.trim()) params.set("fieldroutes_subscription_id", fieldRoutesLifecycleSubscriptionId.trim())
      if (fieldRoutesLifecycleLimit.trim()) params.set("limit", fieldRoutesLifecycleLimit.trim())
      const response = await fetch(`/workflows/${activeWorkflow.id}/fieldroutes_lead_lifecycle_preview?${params.toString()}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as Record<string, unknown>
      if (!response.ok) throw new Error(String(data.error_message || data.error || "FieldRoutes lifecycle preview failed."))
      setFieldRoutesLifecycle(data)
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "FieldRoutes lifecycle preview failed.", "error")
    } finally {
      setFieldRoutesLifecycleLoading(false)
    }
  }, [activeWorkflow?.id, fieldRoutesLifecycleLeadId, fieldRoutesLifecycleLimit, fieldRoutesLifecycleSubscriptionId, pushToast])

  const openFieldRoutesLifecyclePreview = async () => {
    if (!activeWorkflow) return
    setFieldRoutesLifecycleOpen(true)
    await loadFieldRoutesLifecyclePreview()
  }

  const validateGraph = async () => {
    if (!activeWorkflow) {
      setStatus("error")
      setStatusMessage("Save this workflow before validating it.")
      pushToast("Save this workflow before validating it.", "error")
      return
    }
    const nextGraph = normalizeGraphForEditor(editorRef.current ? editorRef.current.serialize() : latestGraphRef.current, stage_options)
    setValidatingGraph(true)
    setStatusMessage("Validating graph...")
    try {
      const formData = new FormData()
      formData.append("graph_json", JSON.stringify(nextGraph))
      const response = await fetch(`/workflows/${activeWorkflow.id}/validate_graph`, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: formData,
      })
      const data = await response.json()
      setValidation(data as WorkflowValidationResult)
      setStatusMessage(data.summary || (data.valid ? "Graph is valid." : "Graph has validation issues."))
      setStatus(data.valid ? "saved" : "error")
      pushToast(data.valid ? "Workflow is valid." : "Workflow has validation issues.", data.valid ? "success" : "error")
      setValidationModalOpen(true)
    } catch {
      setValidation({ valid: false, errors: ["Validation request failed."], warnings: [], summary: "Validation request failed.", graph: nextGraph })
      setStatus("error")
      setStatusMessage("Validation request failed.")
      pushToast("Validation request failed.", "error")
    } finally {
      setValidatingGraph(false)
    }
  }

  const publishGraph = () => {
    if (!activeWorkflow) {
      setStatus("error")
      setStatusMessage("Save this workflow before publishing it.")
      pushToast("Save this workflow before publishing it.", "error")
      return
    }
    const nextGraph = normalizeGraphForEditor(editorRef.current ? editorRef.current.serialize() : latestGraphRef.current, stage_options)
    if (!window.confirm(`Publish ${activeWorkflow.name}? This will make the draft graph the published version.`)) return
    setPublishingGraph(true)
    router.post(`/workflows/${activeWorkflow.id}/publish`, { graph_json: JSON.stringify(nextGraph) }, {
      preserveScroll: true,
      preserveUrl: true,
      replace: true,
      onSuccess: (page) => {
        const props = page.props as { workflow_graph?: { graph_json?: WorkflowGraph } }
        const publishedGraph = cloneWorkflowGraph(props.workflow_graph?.graph_json ?? nextGraph)
        ensureCanonicalBuilderUrl(activeWorkflow.id)
        void applyServerConfirmedGraph(publishedGraph, "publish").then(() => {
          ensureCanonicalBuilderUrl(activeWorkflow.id)
          setSchedulePreviewStaleReason("This preview is no longer current because the workflow changed. Preview eligible leads again.")
          setStatus("saved")
          setStatusMessage("Workflow published.")
          pushToast("Workflow published.", "success")
        })
      },
      onError: () => {
        applyingSavedGraphRef.current = null
        savingOrPublishingRef.current = false
        setStatus("error")
        setStatusMessage("Publish blocked by validation errors.")
        pushToast("Publish blocked by validation errors.", "error")
        setValidationModalOpen(true)
      },
      onFinish: () => setPublishingGraph(false),
    })
  }

  const openLeadPicker = (mode: WorkflowRuntimeMode) => {
    if (!activeWorkflow) {
      setStatus("error")
      setStatusMessage("Save this workflow before running a dry run or internal test.")
      return
    }
    setLeadPickerMode(mode)
    setLeadSearchQuery("")
    setLeadSearchResults([])
    setLeadSearchMessage("Search by customer name, phone, LeadFlow ID, FieldRoutes customer ID, lead ID, or subscription ID.")
  }

  const runGraphDryRun = async () => openLeadPicker("graph_dry_run")

  const runInternalTest = async () => openLeadPicker("internal_test")

  const searchLeads = async () => {
    const query = leadSearchQuery.trim()
    if (query.length < 2) {
      setLeadSearchResults([])
      setLeadSearchMessage("Enter at least 2 characters to search leads.")
      return
    }
    const response = await fetch(`/workflows/lead_search?query=${encodeURIComponent(query)}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
    const data = await response.json() as { results?: WorkflowLeadSearchResult[]; message?: string }
    setLeadSearchResults(data.results ?? [])
    setLeadSearchMessage(data.results?.length ? null : data.message || `No LeadFlow lead was found for “${query}”.`)
  }

  const chooseRuntimeLead = async (lead: WorkflowLeadSearchResult) => {
    if (!leadPickerMode) return
    const nextGraph = normalizeGraphForEditor(editorRef.current ? editorRef.current.serialize() : latestGraphRef.current, stage_options)
    if (leadPickerMode === "internal_test") {
      setInternalTestLead(lead)
      setInternalTestGraph(nextGraph)
      setLeadPickerMode(null)
      return
    }
    await runGraphMode("graph_dry_run", { lead_id: String(lead.id), graph_json: JSON.stringify(nextGraph) })
    setLeadPickerMode(null)
  }

  const confirmInternalTest = async () => {
    if (!internalTestLead || !internalTestGraph) return
    const ok = await runGraphMode("internal_test", { lead_id: String(internalTestLead.id), confirmed: true, graph_json: JSON.stringify(internalTestGraph) })
    if (ok) {
      setInternalTestLead(null)
      setInternalTestGraph(null)
    }
  }

  const runGraphMode = async (mode: WorkflowRuntimeMode, body: Record<string, unknown>) => {
    const url = workflowRuntimeUrl(activeWorkflow?.id, mode)
    if (!url) {
      setStatus("error")
      setStatusMessage("Save this workflow before running a dry run or internal test.")
      return
    }
    setRunningGraphMode(mode)
    setStatusMessage(mode === "graph_dry_run" ? "Running dry run..." : "Running internal test...")
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      })
      const message = response.status === 404 ? runtimeEndpointNotFoundMessage(mode) : await workflowRuntimeResponseMessage(response)
      const ok = response.ok && !/unsupported block|blocked|not found|failed/i.test(message)
      setStatus(ok ? "saved" : "error")
      setStatusMessage(message || "Workflow test finished.")
      pushToast(message || "Workflow test finished.", ok ? "success" : "error")
      if (!ok && import.meta.env.DEV) {
        console.warn("Workflow runtime request failed", { mode, status: response.status, message })
      }
      return ok
    } catch (error) {
      setStatus("error")
      setStatusMessage("Workflow test request failed.")
      pushToast("Workflow test request failed.", "error")
      if (import.meta.env.DEV) console.warn("Workflow runtime request failed", { mode, error })
      return false
    } finally {
      setRunningGraphMode(null)
    }
  }

  const updateAutomationStatus = async (nextStatus: "ready" | "paused") => {
    if (!activeWorkflow) return
    if (nextStatus === "ready" && activeWorkflow.publication_status !== "published") {
      const message = "Publish this workflow before marking it Ready."
      setStatus("error")
      setStatusMessage(message)
      pushToast(message, "error")
      return
    }
    const loadingLabel = nextStatus === "ready" ? "run" : "preview"
    setRunningScheduleAction(loadingLabel)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/automation_status`, {
        method: "PATCH",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ automation_status: nextStatus }),
      })
      const data = await response.json() as { workflow?: WorkflowData; message?: string; error?: string }
      const message = data.message || data.error || "Workflow automation status updated."
      setStatus(response.ok ? "saved" : "error")
      setStatusMessage(message)
      pushToast(message, response.ok ? "success" : "error")
      if (response.ok && data.workflow) setWorkflowOverride(data.workflow)
    } catch {
      const message = "Workflow automation status request failed."
      setStatus("error")
      setStatusMessage(message)
      pushToast(message, "error")
    } finally {
      setRunningScheduleAction(null)
    }
  }

  const previewEligibleLeads = async (options: { occurrenceKey?: string; sourceScheduleRunId?: number | string; filter?: string } = {}) => {
    if (!activeWorkflow) return false
    setRunningScheduleAction("preview")
    setSchedulePreviewError(null)
    const nextGraph = normalizeGraphForEditor(editorRef.current ? editorRef.current.serialize() : latestGraphRef.current, stage_options)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/preview_eligible_leads`, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ graph_json: JSON.stringify(nextGraph), graph_source: activeWorkflow.publication_status === "published" && activeWorkflow.automation_ready ? "published" : "draft", per_page: 25, page: 1, filter: options.filter ?? "all", occurrence_key: options.occurrenceKey, source_schedule_run_id: options.sourceScheduleRunId, remaining_from_run: Boolean(options.sourceScheduleRunId) }),
      })
      const data = await response.json() as Record<string, unknown>
      if (response.ok && data.ok !== false) {
        setSchedulePreview({ ...data, filter: options.filter ?? "all" })
        setSchedulePreviewStaleReason(null)
        setPreviewModalOpen(true)
        setStatus("saved")
        setStatusMessage(String(data.message || "Eligible lead preview complete."))
        pushToast(`Preview complete: ${String(data.ready_count ?? data.eligible_count ?? 0)} leads are ready.`, "success")
        return true
      } else {
        const message = String(data.message || data.error || "Eligible lead preview failed.")
        setSchedulePreviewError(message)
        setStatus("error")
        setStatusMessage(message)
        pushToast(message, "error")
        return false
      }
    } catch {
      const message = "Eligible lead preview request failed."
      setSchedulePreviewError(message)
      setStatus("error")
      setStatusMessage(message)
      pushToast(message, "error")
      return false
    } finally {
      setRunningScheduleAction(null)
    }
  }

  const previewRemainingLeads = async () => {
    if (remainingPreviewLoading) return
    setRemainingPreviewLoading(true)
    const loaded = await previewEligibleLeads({ occurrenceKey: String(scheduleRunResult?.occurrence_key ?? ""), sourceScheduleRunId: String(scheduleRunResult?.schedule_run_id ?? ""), filter: "actionable" })
    if (loaded) setRunResultsOpen(false)
    setRemainingPreviewLoading(false)
  }

  const loadSchedulePreviewPage = async (page: number, filter = String(schedulePreview?.filter ?? "all")) => {
    if (!activeWorkflow || !schedulePreview?.preflight_id) return
    setRunningScheduleAction("preview")
    setSchedulePreviewError(null)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/preview_eligible_leads`, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ preflight_id: schedulePreview.preflight_id, page, per_page: schedulePreview.per_page ?? 25, filter }),
      })
      const data = await response.json() as Record<string, unknown>
      if (response.ok && data.ok !== false) {
        setSchedulePreview({ ...data, filter })
        setStatus("saved")
        setStatusMessage(String(data.message || "Eligible lead preview loaded."))
      } else {
        const message = String(data.message || data.error || "Eligible lead preview page failed.")
        setSchedulePreviewError(message)
        setStatus("error")
        setStatusMessage(message)
        pushToast(message, "error")
      }
    } catch {
      const message = "Eligible lead preview page request failed."
      setSchedulePreviewError(message)
      setStatus("error")
      setStatusMessage(message)
      pushToast(message, "error")
    } finally {
      setRunningScheduleAction(null)
    }
  }

  const openSystemReadiness = async () => {
    if (!activeWorkflow) return
    setSystemReadinessOpen(true)
    setSystemReadinessLoading(true)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/system_readiness`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as Record<string, unknown>
      if (!response.ok) throw new Error(String(data.message || data.error || "System readiness failed to load."))
      setSystemReadiness(data)
    } catch (caught) {
      pushToast(caught instanceof Error ? caught.message : "System readiness failed to load.", "error")
    } finally {
      setSystemReadinessLoading(false)
    }
  }

  const openRunConfirmation = () => {
    if (!activeWorkflow) return
    const message = scheduledRunReadinessMessage(activeWorkflow, schedulePreview, schedulePreviewStaleReason)
    if (message) {
      setScheduleRunError(message)
      setStatus("error")
      setStatusMessage(message)
      pushToast(message, "error")
      return
    }
    setScheduleRunError(null)
    setRunConfirmOpen(true)
  }

  const submitScheduledRun = async () => {
    if (!activeWorkflow || !schedulePreview) return
    setRunningScheduleAction("run")
    setScheduleRunError(null)
    try {
      const response = await fetch(`/workflows/${activeWorkflow.id}/run_scheduled_now`, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ confirmed: true, preflight_id: schedulePreview.preflight_id ?? schedulePreview.schedule_run_id }),
      })
      const data = await response.json() as Record<string, unknown>
      const message = String(data.message || data.error || (response.ok ? "Manual scheduled run complete." : "Manual scheduled run failed."))
      setStatus(response.ok && data.ok !== false ? "saved" : "error")
      setStatusMessage(message)
      pushToast(message, response.ok && data.ok !== false ? "success" : "error")
      if (response.ok && data.ok !== false) {
        setScheduleRunResult(data)
        setSchedulePreview(null)
        setSchedulePreviewStaleReason(null)
        schedulePreviewRef.current = null
        setRunConfirmOpen(false)
        setPreviewModalOpen(false)
        setRunResultsOpen(true)
        const updatedWorkflow = data.workflow as WorkflowData | undefined
        if (updatedWorkflow) setWorkflowOverride(updatedWorkflow)
      } else {
        setScheduleRunError(message)
        if (["preflight_expired", "published_version_changed", "published_graph_changed", "schedule_occurrence_changed", "workflow_changed"].includes(String(data.code ?? ""))) {
          setSchedulePreviewStaleReason(message)
        }
      }
    } catch {
      const message = "Manual scheduled run request failed."
      setScheduleRunError(message)
      setStatus("error")
      setStatusMessage(message)
      pushToast(message, "error")
    } finally {
      setRunningScheduleAction(null)
    }
  }

  const exportWorkflowJson = () => {
    if (!activeWorkflow || !workflow_graph) {
      setStatus("error")
      setStatusMessage("No workflow is open to export.")
      return
    }

    const currentGraph = normalizeGraphForEditor(editorRef.current ? editorRef.current.serialize() : latestGraphRef.current, stage_options)
    if (!currentGraph || !Array.isArray(currentGraph.nodes) || !Array.isArray(currentGraph.connections)) {
      setStatus("error")
      setStatusMessage("No workflow graph is available to export.")
      return
    }

    const payload = buildWorkflowExport({
      workflow: activeWorkflow,
      workflowGraph: workflow_graph,
      graph: currentGraph,
      blocks: blockList,
      validation,
      hasUnpublishedChanges: draftChanged || publishedChanged,
    })
    downloadWorkflowExport(payload, sanitizeWorkflowFilename(activeWorkflow.name))
    setStatus("saved")
    setStatusMessage("Workflow JSON exported.")
  }

  const selectValidationNode = async (nodeId: string) => {
    setInspectorOpen(true)
    await editorRef.current?.selectNode(nodeId)
  }

  const addBlock = async (type: string) => {
    if (!editorRef.current) return
    const nodeId = await editorRef.current.addBlock(type)
    setBlockPickerOpen(false)
    if (nodeId) {
      await editorRef.current.selectNode(nodeId)
    }
  }

  const openCreateWorkflow = () => {
    setCreateWorkflowName("Untitled workflow")
    setCreateWorkflowDescription("")
    setCreateWorkflowIcon(workflow_icon_options[0]?.value ?? "workflow")
    setCreateWorkflowOpen(true)
  }

  const submitCreateWorkflow = () => {
    const defaults = defaultWorkflowForm(trigger_types)
    router.post("/workflows", {
      workflow: {
        ...defaults,
        name: createWorkflowName.trim() || defaults.name,
        description: createWorkflowDescription,
        icon: createWorkflowIcon,
      },
    }, {
      preserveScroll: true,
      onSuccess: () => setCreateWorkflowOpen(false),
    })
  }

  const deleteSelected = async () => {
    if (!selection || selection.kind !== "node" || !editorRef.current) return
    await editorRef.current.deleteNode(selection.id)
  }

  const duplicateSelected = async () => {
    if (!selection || selection.kind !== "node" || !editorRef.current) return
    const nodeId = await editorRef.current.duplicateNode(selection.id)
    if (nodeId) {
      await editorRef.current.selectNode(nodeId)
    }
  }

  const updateSelectedConfig = async (field: WorkflowBlockField, rawValue: string | number | boolean | Record<string, unknown> | unknown[]) => {
    if (!selectedNode || !selectedBlock || !editorRef.current) return
    const currentConfig = selectedNode.config ?? {}
    let nextConfig = { ...currentConfig, [field.key]: normalizeFieldValue(field, rawValue, selectedBlock, currentConfig) }

    if (selectedBlock.type === "condition") {
      if (field.key === "field") {
        const selectedVariable = workflowVariableByKey(String(rawValue))
        const existingOperator = String(currentConfig.operator ?? "")
        const nextOperator = selectedVariable?.operators.includes(existingOperator) ? existingOperator : selectedVariable?.operators[0] ?? "equals"
        nextConfig = { ...nextConfig, operator: nextOperator }
        if (!operatorRequiresValue(nextOperator)) nextConfig.value = null
        else nextConfig.value = normalizeConditionConfig(nextConfig, stage_options.map((stage) => stage.key)).value
      } else if (field.key === "operator") {
        if (!operatorRequiresValue(String(rawValue))) nextConfig.value = null
        else nextConfig.value = normalizeConditionConfig(nextConfig, stage_options.map((stage) => stage.key)).value
      } else {
        nextConfig = normalizeConditionConfig(nextConfig, stage_options.map((stage) => stage.key))
      }
    }

    if (selectedBlock.type === "wait_until" && field.key === "target_type") {
      nextConfig = normalizeWaitUntilConfigForTargetType(nextConfig, String(rawValue || "specific_datetime"))
    }

    await editorRef.current.updateNodeConfig(selectedNode.id, nextConfig)
  }

  const unsupportedBlockCount = blockList.filter((block) => !block.runtime_available).length

  return (
    <>
      <Head title={activeWorkflow ? `${activeWorkflow.name} Builder | LeadFlow` : "Workflows | LeadFlow"}>
        <meta name="description" content="LeadFlow visual workflow builder powered by Rete.js." />
        <meta property="og:title" content={activeWorkflow ? `${activeWorkflow.name} Builder | LeadFlow` : "Workflows | LeadFlow"} />
        <meta property="og:description" content="LeadFlow visual workflow builder powered by Rete.js." />
      </Head>
      <AppShell wide>
        <div className="flex h-[calc(100dvh-4rem)] max-h-[calc(100dvh-4rem)] min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-hairline bg-page shadow-sm">
          <header className="flex shrink-0 flex-col gap-3 border-b border-hairline px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-2xl">{activeWorkflow ? activeWorkflow.name : "Workflows"}</h1>
                <Badge tone={workflowTone}>{activeWorkflow ? humanize(activeWorkflow.status) : "Empty"}</Badge>
                {activeWorkflow && <Badge tone={activeWorkflow.graph_draft_different_from_published ? "signal" : "muted"}>{activeWorkflow.graph_draft_different_from_published ? "Draft changes" : activeWorkflow.graph_state_label}</Badge>}
                {activeWorkflow && <Badge tone="muted">Version {activeWorkflow.version}{activeWorkflow.published_version ? ` · Published v${activeWorkflow.published_version}` : ""}</Badge>}
              </div>
            </div>
            <div className="workflow-scrollbar flex min-w-0 flex-wrap items-center gap-2 lg:flex-nowrap lg:overflow-x-auto">
              <Button type="button" className="shrink-0" onClick={() => setBlockPickerOpen(true)} disabled={!activeWorkflow || !editorReady}>
                <Plus className="h-4 w-4" />
                Add Block
              </Button>
              <Button type="button" variant="secondary" className="shrink-0" onClick={exportWorkflowJson} disabled={!activeWorkflow || !workflow_graph}>
                <Download className="h-4 w-4" />
                Export JSON
              </Button>
              <Button type="button" variant="secondary" className="shrink-0" onClick={saveDraft} disabled={!activeWorkflow || savingGraph || !editorReady}>{savingGraph ? "Saving…" : "Save"}</Button>
              <Button type="button" variant="danger" className="shrink-0" onClick={() => setDeleteWorkflowOpen(true)} disabled={!activeWorkflow}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="secondary" className="shrink-0" disabled={!activeWorkflow || !editorReady}>
                    More
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={!activeWorkflow || validatingGraph || !editorReady} onSelect={() => void validateGraph()}>{validatingGraph ? "Validating…" : "Validate"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || runningGraphMode !== null || !editorReady} onSelect={() => void runGraphDryRun()}>{runningGraphMode === "graph_dry_run" ? "Running dry run…" : "Dry Run"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || runningGraphMode !== null || !editorReady} onSelect={() => void runInternalTest()}>{runningGraphMode === "internal_test" ? "Running test…" : "Test Internal Actions"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || runningScheduleAction !== null || !editorReady} onSelect={() => void previewEligibleLeads()}>{runningScheduleAction === "preview" ? "Previewing leads…" : "Preview Eligible Leads"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || runningScheduleAction !== null || !editorReady} onSelect={openRunConfirmation}>{runningScheduleAction === "run" ? "Running scheduled workflow…" : "Run Scheduled Workflow Now"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || waitingRunsLoading} onSelect={() => void openWaitingRuns()}>{waitingRunsLoading ? "Loading waiting runs…" : "Waiting Runs"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || systemReadinessLoading} onSelect={() => void openSystemReadiness()}>{systemReadinessLoading ? "Loading readiness…" : "System Readiness"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || fieldRoutesReconciliationLoading} onSelect={() => void openFieldRoutesReconciliation()}>{fieldRoutesReconciliationLoading ? "Loading reconciliation…" : "FieldRoutes Reconciliation"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || fieldRoutesLeadSyncLoading} onSelect={() => void openFieldRoutesLeadSyncPreview()}>{fieldRoutesLeadSyncLoading ? "Loading lead sync preview…" : "FieldRoutes Lead Sync Preview"}</DropdownMenuItem>
                  <DropdownMenuItem disabled={!activeWorkflow || fieldRoutesLifecycleLoading} onSelect={() => void openFieldRoutesLifecyclePreview()}>{fieldRoutesLifecycleLoading ? "Loading lifecycle preview…" : "FieldRoutes Lifecycle Preview"}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled={!activeWorkflow || publishingGraph || !editorReady} onSelect={() => publishGraph()}>{publishingGraph ? "Publishing…" : "Publish"}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {activeWorkflow && (
            <AutomationStatusPanel
              workflow={activeWorkflow}
              graph={graphLoaded}
              publishedGraph={workflow_graph?.published_graph_json ?? null}
              preview={schedulePreview}
              previewStaleReason={schedulePreviewStaleReason}
              runningAction={runningScheduleAction}
              onMarkReady={() => void updateAutomationStatus("ready")}
              onPause={() => void updateAutomationStatus("paused")}
              onPreview={() => void previewEligibleLeads()}
              onHistory={() => setHistoryModalOpen(true)}
              onDetails={() => setAutomationDetailsOpen(true)}
            />
          )}

          {(flash_notice || workflow_errors.length > 0 || editorError) && (
            <div className="border-b border-hairline bg-surface px-4 py-2 text-sm">
              <div className="min-w-0 space-y-1">
                {flash_notice && <p className="text-ink-body">{flash_notice}</p>}
                {workflow_errors.length > 0 && <ErrorBox errors={workflow_errors} />}
                {editorError && <ErrorPanel title="Editor failed to load" body={editorError} />}
              </div>
            </div>
          )}

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <WorkflowSidebar
              workflows={workflows}
              activeWorkflowId={activeWorkflow?.id ?? null}
              sidebarOpen={sidebarOpen}
              onToggleSidebar={() => setSidebarOpen((value) => !value)}
              onCreateWorkflow={openCreateWorkflow}
            />

            <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-page">
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-hairline px-4 py-2 text-xs text-ink-muted">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={editorReady ? "accent" : "muted"}>{activeWorkflow ? (editorReady ? "Editor ready" : "Loading editor...") : "Create a workflow to start"}</Badge>
                  {activeWorkflow && savingGraph && <Badge tone="muted">Saving…</Badge>}
                  {activeWorkflow && !savingGraph && draftChanged && <Badge tone="signal">Unsaved changes</Badge>}
                  {activeWorkflow && !savingGraph && !draftChanged && !publishedChanged && <Badge tone="accent">Saved draft</Badge>}
                  {activeWorkflow && !savingGraph && !draftChanged && publishedChanged && <Badge tone="muted" title="Your current draft is saved, but it contains changes that are not part of the published workflow yet.">Draft changes not published</Badge>}
                  {statusMessage && status !== "saving" && <Badge tone={status === "error" ? "signal" : "accent"}>{statusMessage}</Badge>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="muted">{workflows.length} workflows</Badge>
                  {unsupportedBlockCount > 0 && <Badge tone="signal">{unsupportedBlockCount} reserved blocks</Badge>}
                </div>
              </div>

              <div className="flex min-h-0 flex-1 overflow-hidden">
                <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-page">
                  {!activeWorkflow ? (
                    <EmptyCanvasState onCreateWorkflow={openCreateWorkflow} workflows={workflows} />
                  ) : editorError ? (
                    <ErrorPanel title="Workflow editor unavailable" body={editorError} />
                  ) : (
                    <>
                      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-hairline px-4 py-2 text-xs text-ink-muted">
                        <span>{workflow_graph ? (isEmptyWorkflowGraph(graphLoaded) ? "Add a trigger block to start." : "Canvas ready") : "Loading workflow graph..."}</span>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge tone="muted">{activeWorkflow.trigger_label}</Badge>
                          <Badge tone="muted">{activeWorkflow.execution_mode_label}</Badge>
                          <Badge tone={activeWorkflow.enabled ? "accent" : "muted"}>{activeWorkflow.enabled ? "Enabled" : "Disabled"}</Badge>
                        </div>
                      </div>
                      <div className="workflow-builder-canvas flex-1 min-h-0 overflow-hidden rounded-none border-0">
                        <div ref={editorContainerRef} className="h-full min-h-0 w-full" />
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-hairline px-4 py-2 text-xs text-ink-muted">
                        <div className="flex flex-wrap items-center gap-2">
                          <Button type="button" size="sm" variant="secondary" onClick={() => editorRef.current?.undo()} disabled={!editorReady}>Undo</Button>
                          <Button type="button" size="sm" variant="secondary" onClick={() => editorRef.current?.redo()} disabled={!editorReady}>Redo</Button>
                          <Button type="button" size="sm" variant="secondary" onClick={deleteSelected} disabled={!selection || selection.kind !== "node"}>Delete selected</Button>
                          <Button type="button" size="sm" variant="secondary" onClick={duplicateSelected} disabled={!selection || selection.kind !== "node"}>Duplicate selected</Button>
                        </div>
                      </div>
                    </>
                  )}
                </section>

                <InspectorPanel
                  activeWorkflow={activeWorkflow}
                  selectedNode={selectedNode}
                  selectedBlock={selectedBlock}
                  validation={validation}
                  editorReady={editorReady}
                  inspectorOpen={inspectorOpen}
                  onToggle={() => setInspectorOpen((value) => !value)}
                  onUpdateSelectedConfig={updateSelectedConfig}
                  onDeleteSelected={deleteSelected}
                  onDuplicateSelected={duplicateSelected}
                  stageOptions={stage_options}
                  users={users}
                  emailTemplateCatalog={emailTemplateCatalog}
                  emailTemplateCatalogLoading={emailTemplateCatalogLoading}
                  onRefreshEmailTemplates={() => void refreshEmailTemplateCatalog()}
                />
              </div>
            </main>
          </div>
        </div>

        <BlockPickerDialog
          open={blockPickerOpen}
          onOpenChange={setBlockPickerOpen}
          search={blockSearch}
          onSearchChange={setBlockSearch}
          category={blockCategory}
          onCategoryChange={setBlockCategory}
          blocks={filteredBlocks}
          onAddBlock={addBlock}
        />

        <LeadPickerDialog
          open={leadPickerMode !== null}
          mode={leadPickerMode}
          query={leadSearchQuery}
          results={leadSearchResults}
          message={leadSearchMessage}
          onOpenChange={(open) => {
            if (!open) setLeadPickerMode(null)
          }}
          onQueryChange={setLeadSearchQuery}
          onSearch={searchLeads}
          onSelect={chooseRuntimeLead}
        />

        {activeWorkflow && (
          <InternalTestConfirmationDialog
            open={internalTestLead !== null}
            workflow={activeWorkflow}
            lead={internalTestLead}
            graph={internalTestGraph}
            blockMap={blockMap}
            running={runningGraphMode === "internal_test"}
            onOpenChange={(open) => { if (!open && runningGraphMode !== "internal_test") { setInternalTestLead(null); setInternalTestGraph(null) } }}
            onConfirm={() => void confirmInternalTest()}
          />
        )}

        {activeWorkflow && (
          <>
            <AutomationDetailsDialog
              open={automationDetailsOpen}
              onOpenChange={setAutomationDetailsOpen}
              workflow={activeWorkflow}
              graph={graphLoaded}
              publishedGraph={workflow_graph?.published_graph_json ?? null}
              preview={schedulePreview}
            />
            <SchedulePreviewDialog
              open={previewModalOpen}
              onOpenChange={setPreviewModalOpen}
              workflow={activeWorkflow}
              preview={schedulePreview}
              staleReason={schedulePreviewStaleReason}
              error={schedulePreviewError}
              runningAction={runningScheduleAction}
              onPageChange={(page) => void loadSchedulePreviewPage(page)}
              onFilterChange={(filter) => void loadSchedulePreviewPage(1, filter)}
              onPublish={() => publishGraph()}
              onMarkReady={() => void updateAutomationStatus("ready")}
              onRun={openRunConfirmation}
            />
            <ScheduleRunConfirmationDialog
              open={runConfirmOpen}
              onOpenChange={setRunConfirmOpen}
              workflow={activeWorkflow}
              preview={schedulePreview}
              running={runningScheduleAction === "run"}
              error={scheduleRunError}
              onRun={() => void submitScheduledRun()}
            />
            <ScheduleRunResultsDialog
              open={runResultsOpen}
              onOpenChange={setRunResultsOpen}
              result={scheduleRunResult}
              onHistory={() => { setRunResultsOpen(false); setHistoryModalOpen(true) }}
              remainingPreviewLoading={remainingPreviewLoading}
              onPreviewRemaining={() => void previewRemainingLeads()}
            />
            <ScheduleHistoryDialog
              open={historyModalOpen}
              onOpenChange={setHistoryModalOpen}
              workflowId={activeWorkflow.id}
              runs={activeWorkflow.recent_schedule_runs}
            />
            <SystemReadinessDialog
              open={systemReadinessOpen}
              onOpenChange={setSystemReadinessOpen}
              loading={systemReadinessLoading}
              readiness={systemReadiness}
            />
            <FieldRoutesReconciliationDialog
              open={fieldRoutesReconciliationOpen}
              onOpenChange={setFieldRoutesReconciliationOpen}
              loading={fieldRoutesReconciliationLoading}
              data={fieldRoutesReconciliation}
              query={fieldRoutesReconciliationQuery}
              onQueryChange={setFieldRoutesReconciliationQuery}
              mode={fieldRoutesReconciliationMode}
              onModeChange={setFieldRoutesReconciliationMode}
              fieldRoutesCustomerId={fieldRoutesCustomerId}
              onFieldRoutesCustomerIdChange={setFieldRoutesCustomerId}
              fieldRoutesSubscriptionId={fieldRoutesSubscriptionId}
              onFieldRoutesSubscriptionIdChange={setFieldRoutesSubscriptionId}
              onLoad={(page) => void loadFieldRoutesReconciliation(page)}
            />
            <FieldRoutesLeadSyncPreviewDialog
              open={fieldRoutesLeadSyncOpen}
              onOpenChange={setFieldRoutesLeadSyncOpen}
              loading={fieldRoutesLeadSyncLoading}
              data={fieldRoutesLeadSync}
              subscriptionId={fieldRoutesLeadSyncSubscriptionId}
              onSubscriptionIdChange={setFieldRoutesLeadSyncSubscriptionId}
              limit={fieldRoutesLeadSyncLimit}
              onLimitChange={setFieldRoutesLeadSyncLimit}
              onLoad={() => void loadFieldRoutesLeadSyncPreview()}
            />
            <FieldRoutesLifecyclePreviewDialog
              open={fieldRoutesLifecycleOpen}
              onOpenChange={setFieldRoutesLifecycleOpen}
              loading={fieldRoutesLifecycleLoading}
              data={fieldRoutesLifecycle}
              leadId={fieldRoutesLifecycleLeadId}
              onLeadIdChange={setFieldRoutesLifecycleLeadId}
              subscriptionId={fieldRoutesLifecycleSubscriptionId}
              onSubscriptionIdChange={setFieldRoutesLifecycleSubscriptionId}
              limit={fieldRoutesLifecycleLimit}
              onLimitChange={setFieldRoutesLifecycleLimit}
              onLoad={() => void loadFieldRoutesLifecyclePreview()}
            />
            <WaitingRunsDialog
              open={waitingRunsOpen}
              onOpenChange={setWaitingRunsOpen}
              runs={waitingRuns}
              loading={waitingRunsLoading}
              actionId={waitingRunActionId}
              onResume={(run) => void resumeWaitingRun(run)}
              onCancel={(run) => void cancelWaitingRun(run)}
            />
            {validation && (
              <WorkflowValidationDialog
                open={validationModalOpen}
                onOpenChange={setValidationModalOpen}
                validation={validation}
                blockMap={blockMap}
                onSelectNode={(nodeId) => {
                  setValidationModalOpen(false)
                  void selectValidationNode(nodeId)
                }}
              />
            )}
          </>
        )}

        <ToastStack toasts={toasts} onDismiss={(id) => setToasts((items) => items.filter((item) => item.id !== id))} />

        <Dialog open={createWorkflowOpen} onOpenChange={setCreateWorkflowOpen}>
          <DialogContent size="md" className="max-h-[calc(100dvh-2rem)] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Create workflow</DialogTitle>
              <DialogDescription>Name it and pick an icon before opening the builder.</DialogDescription>
            </DialogHeader>
            <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
              <label className="grid gap-2 text-sm">
                Name
                <Input value={createWorkflowName} onChange={(event) => setCreateWorkflowName(event.target.value)} placeholder="Untitled workflow" autoFocus />
              </label>
              <label className="grid gap-2 text-sm">
                Icon
                <Select value={createWorkflowIcon} onChange={(event) => setCreateWorkflowIcon(event.target.value)}>
                  {workflow_icon_options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </label>
              <label className="grid gap-2 text-sm">
                Description
                <textarea className="form-control form-control-textarea" value={createWorkflowDescription} onChange={(event) => setCreateWorkflowDescription(event.target.value)} placeholder="Optional note for your team" />
              </label>
              <div className="rounded-lg border border-hairline bg-page p-3 text-xs text-ink-muted">This creates a draft workflow. Publish only after validation.</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreateWorkflowOpen(false)}>Cancel</Button>
              <Button type="button" onClick={submitCreateWorkflow}>Create workflow</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteWorkflowOpen} onOpenChange={setDeleteWorkflowOpen}>
          <DialogContent size="md" className="max-h-[calc(100dvh-2rem)] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Delete workflow?</DialogTitle>
              <DialogDescription>This will delete this workflow and its workflow run history. This cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-ink-muted">Type DELETE or the workflow name to confirm removal.</p>
              <Input value={deleteWorkflowConfirmation} onChange={(event) => setDeleteWorkflowConfirmation(event.target.value)} placeholder={activeWorkflow?.name ? activeWorkflow.name : "DELETE"} autoFocus />
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDeleteWorkflowOpen(false)}>Cancel</Button>
              <Button
                type="button"
                variant="danger"
                disabled={!activeWorkflow || (deleteWorkflowConfirmation.trim().toUpperCase() !== "DELETE" && deleteWorkflowConfirmation.trim().toLowerCase() !== activeWorkflow.name.trim().toLowerCase())}
                onClick={() => {
                  if (!activeWorkflow) return
                  const confirmation = deleteWorkflowConfirmation.trim()
                  router.delete(`/workflows/${activeWorkflow.id}`, {
                    data: { confirmation },
                    preserveScroll: true,
                    onSuccess: () => {
                      setDeleteWorkflowOpen(false)
                      setDeleteWorkflowConfirmation("")
                    },
                  })
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete workflow
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AppShell>
    </>
  )
}

function AutomationStatusPanel({ workflow, graph, publishedGraph, preview, previewStaleReason, runningAction, onMarkReady, onPause, onPreview, onHistory, onDetails }: {
  workflow: WorkflowData
  graph: WorkflowGraph
  publishedGraph: WorkflowGraph | null
  preview: Record<string, unknown> | null
  previewStaleReason: string | null
  runningAction: "preview" | "run" | null
  onMarkReady: () => void
  onPause: () => void
  onPreview: () => void
  onHistory: () => void
  onDetails: () => void
}) {
  const draftSchedule = scheduleInfoFromGraph(graph)
  const publishedSchedule = scheduleInfoFromGraph(publishedGraph)
  const primarySchedule = workflow.publication_status === "published" ? publishedSchedule : draftSchedule
  const canMarkReady = workflow.publication_status === "published" && !workflow.automation_ready && runningAction === null
  const canPause = workflow.publication_status === "published" && workflow.automation_ready && runningAction === null
  const helper = workflow.publication_status !== "published"
    ? "Publish before manual execution"
    : workflow.automation_ready
      ? "Ready for manual scheduled runs"
      : "Paused until marked Ready"
  const lastRun = workflow.recent_schedule_runs?.[0]

  return (
    <div className="border-b border-hairline bg-surface px-4 py-3 text-sm">
      <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-ink-display">Automation</p>
            <Badge tone={workflow.status === "published" ? "accent" : "muted"}>{humanize(workflow.publication_status || workflow.status)}</Badge>
            <Badge tone={workflow.automation_ready ? "accent" : "signal"}>{workflow.automation_ready ? "Ready" : "Paused"}</Badge>
            <Badge tone={workflow.global_automation_enabled ? "accent" : "signal"}>{workflow.global_automation_enabled ? "Automatic runs on" : "Automatic runs off"}</Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
            <span>{primarySchedule?.label ?? "Schedule not configured"}</span>
            <span>{helper}</span>
            {lastRun && <span>{lastRun.execution_mode === "eligibility_preview" ? "Last preview" : "Last manual run"}: {compactScheduleRunSummary(lastRun)}</span>}
            {preview && <span>{previewStaleReason ? "Preview stale" : `Preview: ${String(preview.ready_count ?? preview.eligible_count ?? 0)} ready · ${String(preview.will_process_count ?? preview.ready_count ?? preview.eligible_count ?? 0)} will process`}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onMarkReady} disabled={!canMarkReady}>{runningAction === "run" ? "Marking ready…" : "Mark Ready"}</Button>
          <Button type="button" size="sm" variant="secondary" onClick={onPause} disabled={!canPause}>{runningAction === "preview" ? "Pausing…" : "Pause"}</Button>
          <Button type="button" size="sm" variant="secondary" onClick={onPreview} disabled={runningAction !== null}>{runningAction === "preview" ? "Previewing…" : "Preview Leads"}</Button>
          <Button type="button" size="sm" variant="secondary" onClick={onHistory}>History</Button>
          <Button type="button" size="sm" variant="secondary" onClick={onDetails}>Details</Button>
        </div>
      </div>
      {workflow.publication_status !== "published" && <p className="mt-2 text-xs text-signal-display">Publish this workflow before marking it Ready.</p>}
    </div>
  )
}

function AutomationDetailsDialog({ open, onOpenChange, workflow, graph, publishedGraph, preview }: { open: boolean; onOpenChange: (open: boolean) => void; workflow: WorkflowData; graph: WorkflowGraph; publishedGraph: WorkflowGraph | null; preview: Record<string, unknown> | null }) {
  const draftSchedule = scheduleInfoFromGraph(graph)
  const publishedSchedule = scheduleInfoFromGraph(publishedGraph)
  const publishedScheduleLabel = publishedScheduleDisplay(workflow, publishedGraph)
  const nextDue = workflow.publication_status === "published" && publishedSchedule ? nextOccurrenceLabel(publishedSchedule.minutes) : "Available after publishing"
  const schedulesMatch = draftSchedule && publishedSchedule && draftSchedule.minutes === publishedSchedule.minutes && draftSchedule.timezone === publishedSchedule.timezone

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,54rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Automation Details</DialogTitle>
          <DialogDescription>Manual previews and manual scheduled runs are available to admins.</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <DetailItem label="Workflow status" value={humanize(workflow.publication_status || workflow.status)} />
            <DetailItem label="Workflow automation" value={workflow.automation_ready ? "Ready" : "Paused"} />
            <DetailItem label="Global automation" value={workflow.global_automation_enabled ? "Enabled" : "Disabled"} />
            <DetailItem label="Draft schedule" value={draftSchedule?.label ?? "Not configured"} />
            <DetailItem label="Published schedule" value={schedulesMatch ? `Draft and published schedules match: ${publishedSchedule?.label}` : publishedScheduleLabel} />
            <DetailItem label="Next calculated occurrence" value={nextDue} />
            <DetailItem label="Last manual run" value={formatDateTime(workflow.last_manual_scheduled_run_at) || "None"} />
            <DetailItem label="Last automatic run" value={formatDateTime(workflow.last_automatic_run_at) || "None"} />
            <DetailItem label="Pause reason" value={workflow.pause_reason || "None"} />
            <DetailItem label="Maximum leads per run" value={String(workflow.max_leads_per_run)} />
            <DetailItem label="Overlap policy" value="Skip if previous run is active" />
            <DetailItem label="Current preflight" value={preview?.preflight_id ? `#${String(preview.preflight_id)} expires ${formatDateTime(preview.expires_at) || "soon"}` : "No active preview"} />
          </div>
          <div className="mt-4 rounded-lg border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">{workflow.global_automation_message}</div>
        </div>
        <DialogFooter><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border border-hairline bg-page p-3"><p className="text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</p><p className="mt-1 text-ink-display">{value}</p></div>
}

function SchedulePreviewDialog({ open, onOpenChange, workflow, preview, staleReason, error, runningAction, onPageChange, onFilterChange, onPublish, onMarkReady, onRun }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workflow: WorkflowData
  preview: Record<string, unknown> | null
  staleReason: string | null
  error: string | null
  runningAction: "preview" | "run" | null
  onPageChange: (page: number) => void
  onFilterChange: (filter: string) => void
  onPublish: () => void
  onMarkReady: () => void
  onRun: () => void
}) {
  const results = previewResults(preview)
  const canRun = workflow.publication_status === "published" && workflow.automation_ready && Boolean(preview?.preflight_id) && !staleReason && Number(preview?.blocked_count ?? 0) === 0 && Number(preview?.will_process_count ?? preview?.ready_count ?? preview?.eligible_count ?? 0) > 0
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,56rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{String(preview?.execution_mode ?? "eligibility_preview") === "manual_scheduled_run" ? "Scheduled Run Results" : "Eligible Lead Preview"}</DialogTitle>
          <DialogDescription>{String(preview?.execution_mode ?? "eligibility_preview") === "manual_scheduled_run" ? "Manual Scheduled Run — changes may have been made." : "Preview only — no changes will be made."}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden">
          {preview ? (
            <div className="flex h-full min-h-0 flex-col gap-3">
              <div className="shrink-0 space-y-3 bg-page">
                <PreviewSummary preview={preview} />
                {staleReason && <div className="rounded-md border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">{staleReason}</div>}
                {error && <div className="rounded-md border border-danger/30 bg-danger/10 p-3 text-xs text-danger-display">{error}</div>}
                {Boolean(preview.preflight_id) && <p className="text-xs text-ink-muted">Preflight #{String(preview.preflight_id)} expires {formatDateTime(preview.expires_at) || "soon"}.</p>}
                <PreviewFilters preview={preview} onPageChange={onPageChange} onFilterChange={onFilterChange} disabled={runningAction === "preview"} />
              </div>
              <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1" tabIndex={0}>
                {results.length > 0 ? <PreviewLeadRows results={results} /> : <p className="rounded-md border border-hairline bg-surface p-3 text-sm text-ink-muted">No preview rows on this page.</p>}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-muted">Preview eligible leads to see matching leads here.</div>
          )}
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
          {String(preview?.execution_mode ?? "eligibility_preview") === "manual_scheduled_run" ? null : workflow.publication_status !== "published" ? (
            <Button type="button" onClick={onPublish}>Publish Workflow</Button>
          ) : !workflow.automation_ready ? (
            <Button type="button" onClick={onMarkReady}>Mark Ready</Button>
          ) : (
            <Button type="button" onClick={onRun} disabled={!canRun}>Continue to Run Confirmation</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PreviewSummary({ preview }: { preview: Record<string, unknown> }) {
  const mode = String(preview.execution_mode ?? "eligibility_preview")
  const items: Array<[string, unknown]> = mode === "manual_scheduled_run" ? [
    ["Candidates", preview.candidate_count],
    ["Qualified", preview.qualified_count ?? preview.eligible_count],
    ["Processed", preview.processed_count],
    ["Completed", preview.completed_count],
    ["Failed", preview.failed_count],
    ["Skipped", preview.skipped_count],
  ] : [
    ["Candidates", preview.candidate_count],
    ["Qualified", preview.qualified_count ?? preview.eligible_count],
    ["Ready now", preview.ready_count ?? preview.eligible_count],
    ["Will process", preview.will_process_count ?? preview.ready_count ?? preview.eligible_count],
    ["Deferred", preview.deferred_count],
    ["Already completed", preview.already_completed_count],
    ["Blocked", preview.blocked_count],
    ["Actions", preview.planned_action_count],
  ]
  return <PreviewSummaryGrid items={items} />
}

function PreviewSummaryGrid({ items }: { items: Array<[string, unknown]> }) {
  return (
    <div className="grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
      {items.map(([label, value]) => <PreviewSummaryCard key={label} label={label} value={value} />)}
    </div>
  )
}

function PreviewSummaryCard({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex min-h-24 flex-col justify-between rounded-lg border border-hairline bg-page p-3">
      <p className="min-h-10 text-sm leading-5 text-ink-muted">{label}</p>
      <p className="mt-2 font-mono text-2xl font-semibold leading-none tabular-nums text-ink-display">{String(value ?? 0)}</p>
    </div>
  )
}

function PreviewFilters({ preview, onPageChange, onFilterChange, disabled }: { preview: Record<string, unknown>; onPageChange: (page: number) => void; onFilterChange: (filter: string) => void; disabled: boolean }) {
  const page = Number(preview.page ?? 1)
  const totalPages = Number(preview.total_pages ?? 1)
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline bg-surface p-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["all", "All"],
          ["ready", "Ready"],
          ["already_completed", "Already Completed"],
          ["deferred", "Deferred"],
          ["blocked", "Blocked"],
          ["not_qualified", "Not Qualified"],
        ].map(([filter, label]) => (
          <Button key={filter} type="button" size="sm" variant={String(preview.filter ?? "all") === filter ? "primary" : "secondary"} disabled={disabled} onClick={() => onFilterChange(filter)}>{label}</Button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="secondary" disabled={disabled || page <= 1} onClick={() => onPageChange(page - 1)}>Previous</Button>
        <span className="text-ink-muted">Page {page} of {totalPages}</span>
        <Button type="button" size="sm" variant="secondary" disabled={disabled || page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</Button>
      </div>
    </div>
  )
}

function PreviewLeadRows({ results }: { results: Array<Record<string, unknown>> }) {
  return <div className="grid gap-2">{results.map((item) => <PreviewLeadRow key={String(item.lead_id)} item={item} />)}</div>
}

function PreviewLeadRow({ item }: { item: Record<string, unknown> }) {
  const actions = Array.isArray(item.planned_actions) ? item.planned_actions as Array<Record<string, unknown> | string> : []
  const actionSummary = actions.map((action) => typeof action === "string" ? action : String(action.summary ?? action.node_type ?? "Action")).join(" · ")
  return (
    <details className="rounded-md border border-hairline bg-surface p-3 text-xs">
      <summary className="cursor-pointer list-none">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-ink-display">{String(item.customer_name ?? "Lead")}</p>
            <p className="mt-1 text-ink-muted">{String(item.stage_name ?? item.stage_key ?? "Unknown stage")} · LeadFlow #{String(item.lead_id)}</p>
            <p className="mt-1 text-ink-muted">
              {Boolean(item.fieldroutes_customer_id) && <span>FR Customer #{String(item.fieldroutes_customer_id)} · </span>}
              {Boolean(item.fieldroutes_lead_id) && <span>FR Lead #{String(item.fieldroutes_lead_id)} · </span>}
              {Boolean(item.fieldroutes_subscription_id) && <span>FR Subscription #{String(item.fieldroutes_subscription_id)}</span>}
            </p>
            {actionSummary && <p className="mt-1 text-ink-muted">{actionSummary}</p>}
          </div>
          <Badge tone={item.qualification_status === "ready" ? "accent" : item.qualification_status === "blocked" ? "signal" : "muted"}>{previewStatusLabel(item)}</Badge>
        </div>
      </summary>
      <div className="mt-3 border-t border-hairline pt-3 text-ink-muted">
        {Boolean(item.blocked_reason) && <p className="text-signal-display">Blocked: {String(item.blocked_reason)}</p>}
        {Boolean(item.completed_at) && <p>Completed at: {formatDateTime(item.completed_at) || String(item.completed_at)}</p>}
        {Boolean(item.prior_workflow_run_id) && <p>Prior workflow run: #{String(item.prior_workflow_run_id)}</p>}
        {actions.length > 0 && <p>Planned actions: {actionSummary}</p>}
        {Array.isArray(item.condition_path) && item.condition_path.length > 0 && <pre className="mt-2 overflow-x-auto rounded bg-page p-2 text-[11px]">{JSON.stringify(item.condition_path, null, 2)}</pre>}
      </div>
    </details>
  )
}

function ScheduleRunConfirmationDialog({ open, onOpenChange, workflow, preview, running, error, onRun }: { open: boolean; onOpenChange: (open: boolean) => void; workflow: WorkflowData; preview: Record<string, unknown> | null; running: boolean; error: string | null; onRun: () => void }) {
  const counts = plannedActionCounts(preview)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="flex h-[min(90dvh,42rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Run Scheduled Workflow Now</DialogTitle>
          <DialogDescription>This is a one-time manual admin execution using the current preview preflight.</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 text-sm">
          <div className="grid gap-2 rounded-lg border border-hairline bg-page p-3 text-xs">
            <span>Workflow: {workflow.name}</span>
            <span>Published version: {String(workflow.published_version ?? workflow.version)}</span>
            <span>Candidates: {String(preview?.candidate_count ?? 0)}</span>
            <span>Qualified: {String(preview?.qualified_count ?? preview?.eligible_count ?? 0)}</span>
            <span>Ready now: {String(preview?.ready_count ?? preview?.eligible_count ?? 0)}</span>
            <span>Will process this run: {String(preview?.will_process_count ?? preview?.ready_count ?? preview?.eligible_count ?? 0)}</span>
            <span>Deferred by run limit: {String(preview?.deferred_count ?? 0)}</span>
            <span>Planned actions: {String(preview?.planned_action_count ?? 0)}</span>
            <span>Preflight expires: {formatDateTime(preview?.expires_at) || "soon"}</span>
          </div>
          <div className="mt-3 rounded-lg border border-hairline bg-surface p-3 text-xs">
            <p className="font-medium text-ink-display">This run will process {String(preview?.will_process_count ?? preview?.ready_count ?? preview?.eligible_count ?? 0)} of {String(preview?.ready_count ?? preview?.eligible_count ?? 0)} ready leads.</p>
            {Number(preview?.deferred_count ?? 0) > 0 && <p className="mt-1 text-ink-muted">{String(preview?.deferred_count)} leads will remain available for a later run.</p>}
            <p className="mt-3 font-medium text-ink-display">This run may:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-muted">
              <li>Create {counts.notes} internal notes</li>
              <li>Move {counts.moves} pipeline stages</li>
              <li>Create {counts.tasks} tasks or follow-ups</li>
            </ul>
          </div>
          <div className="mt-3 rounded-lg border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">No webhooks, email, SMS, CTM calls, or unattended schedules will run.</div>
          {error && <div className="mt-3 rounded-lg border border-danger/30 bg-danger/10 p-3 text-xs text-danger-display">{error}</div>}
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" disabled={running} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" disabled={running} onClick={onRun}>{running ? "Running workflow…" : "Confirm Run"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ScheduleRunResultsDialog({ open, onOpenChange, result, onHistory, onPreviewRemaining, remainingPreviewLoading }: { open: boolean; onOpenChange: (open: boolean) => void; result: Record<string, unknown> | null; onHistory: () => void; onPreviewRemaining: () => void; remainingPreviewLoading: boolean }) {
  const rows = previewResults(result)
  const hasRemaining = Number(result?.ready_count ?? 0) > 0 || Number(result?.deferred_count ?? 0) > 0 || Number(result?.retryable_failed_count ?? 0) > 0
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,56rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Manual Scheduled Run Results</DialogTitle>
          <DialogDescription>Manual Scheduled Run — changes may have been made.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden">
          {result ? (
            <div className="flex h-full min-h-0 flex-col gap-3">
              <div className="shrink-0 space-y-3 bg-page">
                <PreviewSummary preview={result} />
                <div className="rounded-lg border border-hairline bg-surface p-3 text-xs text-ink-muted">
                  <p>Occurrence: {String(result.occurrence_key ?? "—")}</p>
                  <p>Processed: {String(result.processed_count ?? 0)} · Completed: {String(result.completed_count ?? 0)} · Failed: {String(result.failed_count ?? 0)} · Skipped: {String(result.skipped_count ?? 0)}</p>
                  {Number(result.deferred_count ?? 0) > 0 && <p>{String(result.deferred_count)} leads remain deferred by the run limit.</p>}
                </div>
              </div>
              <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1" tabIndex={0}>
                {rows.length > 0 ? <PreviewLeadRows results={rows} /> : <p className="rounded-md border border-hairline bg-surface p-3 text-sm text-ink-muted">No per-lead result rows were returned.</p>}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-muted">Run a manual scheduled workflow to see results.</div>
          )}
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
          <Button type="button" variant="secondary" onClick={onHistory}>View History</Button>
          {hasRemaining && <Button type="button" onClick={onPreviewRemaining} disabled={remainingPreviewLoading} aria-busy={remainingPreviewLoading}>{remainingPreviewLoading ? "Loading remaining leads…" : "Preview Remaining Leads"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ScheduleHistoryDialog({ open, onOpenChange, workflowId, runs }: { open: boolean; onOpenChange: (open: boolean) => void; workflowId: number; runs: WorkflowData["recent_schedule_runs"] }) {
  const [history, setHistory] = React.useState<{ runs: WorkflowScheduleRunData[]; page: number; per_page: number; total_pages: number; total_count: number } | null>(null)
  const [statusFilter, setStatusFilter] = React.useState("")
  const [modeFilter, setModeFilter] = React.useState("")
  const [graphSourceFilter, setGraphSourceFilter] = React.useState("")
  const [dateFromFilter, setDateFromFilter] = React.useState("")
  const [dateToFilter, setDateToFilter] = React.useState("")
  const [perPage, setPerPage] = React.useState("25")
  const [query, setQuery] = React.useState("")
  const [loading, setLoading] = React.useState(false)
  const currentRuns = history?.runs ?? runs
  const page = history?.page ?? 1
  const totalPages = history?.total_pages ?? 1

  const loadHistory = React.useCallback(async (nextPage = 1) => {
    setLoading(true)
    const params = new URLSearchParams({ page: String(nextPage), per_page: perPage })
    if (statusFilter) params.set("status", statusFilter)
    if (modeFilter) params.set("execution_mode", modeFilter)
    if (graphSourceFilter) params.set("graph_source", graphSourceFilter)
    if (dateFromFilter) params.set("date_from", dateFromFilter)
    if (dateToFilter) params.set("date_to", dateToFilter)
    if (query.trim()) params.set("query", query.trim())
    try {
      const response = await fetch(`/workflows/${workflowId}/schedule_runs?${params.toString()}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json() as { runs: WorkflowScheduleRunData[]; page: number; per_page: number; total_pages: number; total_count: number }
      if (!response.ok) throw new Error("Workflow history failed to load.")
      setHistory(data)
    } finally {
      setLoading(false)
    }
  }, [workflowId, statusFilter, modeFilter, graphSourceFilter, dateFromFilter, dateToFilter, perPage, query])

  React.useEffect(() => { if (open) void loadHistory(1) }, [open, loadHistory])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,54rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Workflow Run History</DialogTitle>
          <DialogDescription>Filterable audit trail for previews, manual scheduled runs, per-lead evaluations, and action runs.</DialogDescription>
        </DialogHeader>
        <div className="shrink-0 rounded-lg border border-hairline bg-page p-3 text-xs">
          <div className="grid gap-2 md:grid-cols-4">
            <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">All statuses</option><option value="completed">Completed</option><option value="completed_with_deferred">Completed with deferred</option><option value="partially_failed">Partially failed</option><option value="failed">Failed</option><option value="skipped">Skipped</option><option value="blocked">Blocked</option><option value="preview">Preview</option></Select>
            <Select value={modeFilter} onChange={(event) => setModeFilter(event.target.value)}><option value="">All modes</option><option value="eligibility_preview">Eligibility Preview</option><option value="manual_scheduled_run">Manual Scheduled Run</option><option value="retry_failed">Retry</option></Select>
            <Select value={graphSourceFilter} onChange={(event) => setGraphSourceFilter(event.target.value)}><option value="">All graph sources</option><option value="draft">Draft</option><option value="published">Published</option></Select>
            <Select value={perPage} onChange={(event) => setPerPage(event.target.value)}><option value="25">25 per page</option><option value="50">50 per page</option><option value="100">100 per page</option></Select>
            <Input type="date" value={dateFromFilter} aria-label="Date from" onChange={(event) => setDateFromFilter(event.target.value)} />
            <Input type="date" value={dateToFilter} aria-label="Date to" onChange={(event) => setDateToFilter(event.target.value)} />
            <Input value={query} placeholder="Search lead/customer/ID" onChange={(event) => setQuery(event.target.value)} />
            <Button type="button" variant="secondary" onClick={() => void loadHistory(1)} disabled={loading}>{loading ? "Loading…" : "Apply filters"}</Button>
          </div>
        </div>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
          {currentRuns.length > 0 ? <ScheduleHistory workflowId={workflowId} runs={currentRuns} /> : <p className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-muted">No workflow history matched the current filters.</p>}
        </div>
        <DialogFooter className="items-center justify-between gap-2">
          <div className="text-xs text-ink-muted">Page {page} of {totalPages} · {history?.total_count ?? runs.length} runs</div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => void loadHistory(page - 1)} disabled={loading || page <= 1}>Previous</Button>
            <Button type="button" variant="secondary" onClick={() => void loadHistory(page + 1)} disabled={loading || page >= totalPages}>Next</Button>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ScheduleHistory({ workflowId, runs }: { workflowId: number; runs: WorkflowData["recent_schedule_runs"] }) {
  return (
    <div className="grid gap-2 text-xs">
      {runs.map((run) => <ScheduleHistoryRun key={run.id} workflowId={workflowId} run={run} />)}
    </div>
  )
}

function ScheduleHistoryRun({ workflowId, run }: { workflowId: number; run: WorkflowScheduleRunData }) {
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [detail, setDetail] = React.useState<WorkflowScheduleRunDetailData | null>(null)

  React.useEffect(() => {
    if (!open || detail || loading) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`/workflows/${workflowId}/schedule_runs/${run.id}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(async (response) => {
        const data = await response.json() as WorkflowScheduleRunDetailData & { error?: string; message?: string }
        if (!response.ok) throw new Error(String(data.message || data.error || "Workflow history details failed to load."))
        if (!cancelled) setDetail(data)
      })
      .catch((caught: unknown) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "Workflow history details failed to load.") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, detail, loading, workflowId, run.id])

  return (
    <details open={open} onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)} className="rounded-md border border-hairline bg-surface p-3">
      <summary className="cursor-pointer list-none">
        <div className="grid gap-2 md:grid-cols-6 md:items-center">
          <span>{formatDateTime(run.started_at) || "Not started"}</span>
          <Badge tone={run.status === "completed" ? "accent" : run.status === "blocked" || run.status === "failed" || run.status === "partially_failed" ? "signal" : "muted"}>{humanize(run.status)}</Badge>
          <span>{humanize(run.execution_mode)}</span>
          <span>Candidates: {run.candidate_count}</span>
          <span>Qualified: {String(run.metadata?.qualified_count ?? Number(run.eligible_count) + Number(run.metadata?.blocked_count ?? 0))}</span>
          {run.execution_mode === "eligibility_preview" || run.execution_mode === "dry_run" ? <span>No changes made</span> : <span>Completed/Failed: {run.completed_count}/{run.failed_count}</span>}
        </div>
      </summary>
      <div className="mt-3 border-t border-hairline pt-3 text-ink-muted">
        <div className="grid gap-1 md:grid-cols-2">
          <p>Workflow version: {String(run.workflow_version ?? "—")}</p>
          <p>Ready: {String(run.metadata?.ready_count ?? run.eligible_count)}</p>
          <p>Will process: {String(run.metadata?.will_process_count ?? run.processed_count)}</p>
          <p>Already completed: {String(run.metadata?.already_completed_count ?? 0)}</p>
          <p>Deferred: {String(run.metadata?.deferred_count ?? 0)}</p>
          <p>Blocked: {String(run.metadata?.blocked_count ?? 0)}</p>
          <p>Skipped: {run.skipped_count}</p>
          <p>Initiated by: {run.initiated_by_email || "System"}</p>
        </div>
        <p className="mt-2 break-all">Occurrence: {run.occurrence_key}</p>
        {run.error_message && <p className="mt-2 text-danger-display">{run.error_message}</p>}
        {loading && <p className="mt-3 rounded-md border border-hairline bg-page p-3">Loading run details…</p>}
        {error && <p className="mt-3 rounded-md border border-danger/30 bg-danger/10 p-3 text-danger-display">{error}</p>}
        {detail && <ScheduleHistoryDetail detail={detail} />}
      </div>
    </details>
  )
}

function ScheduleHistoryDetail({ detail }: { detail: WorkflowScheduleRunDetailData }) {
  return (
    <div className="mt-3 space-y-3">
      <div className="rounded-md border border-hairline bg-page p-3">
        <p className="font-medium text-ink-display">Readiness and capabilities</p>
        <div className="mt-2 grid gap-1 md:grid-cols-2">
          <p>Graph source: {humanize(detail.graph_source)}</p>
          <p>Published version: {String(detail.published_version ?? "—")}</p>
          <p>Duration: {detail.duration_ms == null ? "—" : `${detail.duration_ms} ms`}</p>
          <p>Actions planned: {detail.planned_action_count}</p>
          <p>Automatic scheduling: {detail.capabilities.automatic_scheduling_enabled ? "Enabled" : "Disabled"}</p>
          <p>External actions: {detail.capabilities.external_actions_enabled ? "Enabled" : "Disabled"}</p>
          <p>Webhooks/SMS/Email/CTM: Disabled</p>
          <p>AI generation: Disabled</p>
          <p>Retryable: {detail.retryability?.retryable ? "Yes — review required" : "No"}</p>
          <p>{detail.retryability?.reason || "Retry support is disabled."}</p>
        </div>
        <Button type="button" variant="secondary" size="sm" disabled className="mt-3">Retry support coming next</Button>
      </div>
      <div className="space-y-2">
        {detail.runs.length > 0 ? detail.runs.map((run) => (
          <div key={run.id} className="rounded-md border border-hairline bg-page p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium text-ink-display">{run.lead?.customer_name || "Workflow run"}</p>
                <p>{run.lead?.stage_name || "No lead"}</p>
              </div>
              <Badge tone={run.status === "completed" ? "accent" : run.status === "failed" ? "signal" : "muted"}>{humanize(run.status)}</Badge>
            </div>
            <p className="mt-2">{run.action_summary}</p>
            {run.lead?.view_url && <a className="mt-2 inline-flex text-accent-display underline" href={run.lead.view_url}>View lead</a>}
            {run.error_message && <p className="mt-2 text-danger-display">{run.error_message}</p>}
            <div className="mt-3 space-y-1">
              {run.actions.map((action) => (
                <div key={action.id} className="rounded border border-hairline bg-surface p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>{action.summary}</span>
                    <Badge tone={action.status === "executed" || action.status === "evaluated" ? "accent" : action.status === "failed" ? "signal" : "muted"}>{humanize(action.status)}</Badge>
                  </div>
                  <p className="mt-1 text-ink-muted">{action.node_type} · Node {action.node_id}</p>
                  {action.error_message && <p className="mt-1 text-danger-display">{action.error_message}</p>}
                  <ActionOutputSummary action={action} />
                </div>
              ))}
            </div>
          </div>
        )) : <p className="rounded-md border border-hairline bg-page p-3">No per-lead workflow runs were recorded.</p>}
      </div>
    </div>
  )
}

function ActionOutputSummary({ action }: { action: WorkflowActionRunDetailData }) {
  const output = action.output || {}
  if (Object.keys(output).length === 0) return null
  if (action.node_type === "pipeline.move_stage") {
    return <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2"><span>Previous: {String(output.previous_stage ?? "—")}</span><span>New: {String(output.new_stage ?? "—")}</span><span>Stage history #{String(output.stage_history_id ?? "—")}</span><span>Changed: {String(output.changed ?? false)}</span></div>
  }
  if (action.node_type === "task.create") {
    return <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2"><span>Task #{String(output.task_id ?? output.follow_up_id ?? "—")}</span><span>{String(output.title ?? "Untitled task")}</span><span>Assignee: {String(output.assigned_user_name ?? output.assigned_user_id ?? "Unassigned")}</span><span>Due: {String(output.due_at ?? "—")}</span></div>
  }
  if (action.node_type === "create_follow_up") {
    return <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2"><span>Follow-up #{String(output.follow_up_id ?? "—")}</span><span>{String(output.description ?? "Follow-up")}</span><span>Assignee: {String(output.assigned_user_name ?? output.assigned_user_id ?? "Unassigned")}</span><span>Due: {String(output.due_at ?? "—")}</span></div>
  }
  if (action.node_type === "create_internal_note") {
    return <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2"><span>Note #{String(output.note_id ?? "—")}</span><span>Category: {String(output.category ?? "—")}</span><span>Pinned: {String(output.pinned ?? false)}</span><span>Created: {String(output.created_at ?? "—")}</span></div>
  }
  return <p className="mt-1 break-all text-ink-muted">Output: {JSON.stringify(output)}</p>
}

function WaitingRunsDialog({ open, onOpenChange, runs, loading, actionId, onResume, onCancel }: { open: boolean; onOpenChange: (open: boolean) => void; runs: Record<string, unknown>[]; loading: boolean; actionId: string | null; onResume: (run: Record<string, unknown>) => void; onCancel: (run: Record<string, unknown>) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex min-h-0 max-h-[calc(100dvh-2rem)] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Waiting Workflow Runs</DialogTitle>
          <DialogDescription>Delay and Wait Until runs are auditable here. Due waiting runs normally resume automatically within approximately one minute.</DialogDescription>
        </DialogHeader>
        <div className="workflow-scrollbar min-h-0 flex-1 overflow-y-auto pr-1 text-sm">
          {loading ? <p className="rounded-md border border-hairline bg-page p-3">Loading waiting runs…</p> : runs.length === 0 ? (
            <p className="rounded-md border border-hairline bg-page p-3 text-ink-muted">No waiting workflow runs.</p>
          ) : (
            <div className="grid gap-2">
              {runs.map((run) => {
                const lead = run.lead as Record<string, unknown> | null | undefined
                const waitingNode = run.waiting_node as Record<string, unknown> | null | undefined
                return (
                  <section key={String(run.id)} className="rounded-md border border-hairline bg-page p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-ink-display">{String(run.workflow_name ?? "Workflow")} · {humanize(String(run.wait_type ?? "wait"))}</p>
                        <p className="text-xs text-ink-muted">{lead ? `${String(lead.customer_name ?? "Lead")} · Lead #${String(lead.id ?? "—")}` : "Lead unavailable"}</p>
                      </div>
                      <Badge tone={String(run.status) === "waiting" ? "signal" : "muted"}>{humanize(String(run.status ?? "waiting"))}</Badge>
                    </div>
                    <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2">
                      <p>Waiting block: {String(run.node_type ?? "—")} / {String(run.node_id ?? "—")}</p>
                      <p>Resume time: {formatDateTime(String(run.resume_at ?? "")) || "—"}</p>
                      <p>Due: {String(run.due_label ?? "—")}</p>
                      <p>Timezone: {String(run.timezone ?? "—")}</p>
                      <p>Waiting version: {String(run.workflow_version ?? "—")}</p>
                      <p>Current version: {String(run.current_workflow_version ?? "—")}{run.workflow_version_differs ? " · differs" : ""}</p>
                      <p className="break-all">Occurrence: {String(run.occurrence_key ?? "—")}</p>
                      <p>Next nodes: {Array.isArray(waitingNode?.next_node_ids) ? waitingNode?.next_node_ids.join(", ") : "—"}</p>
                      {Boolean(run.resumed_at) && <p>Resumed: {formatDateTime(String(run.resumed_at))} by {String(run.resumed_by_email ?? "admin")}</p>}
                      {Boolean(run.cancelled_at) && <p>Cancelled: {formatDateTime(String(run.cancelled_at))} by {String(run.cancelled_by_email ?? "admin")}</p>}
                      <p>Auto attempts: {String(run.auto_resume_attempt_count ?? 0)}</p>
                      {Boolean(run.last_auto_resume_attempt_at) && <p>Last auto attempt: {formatDateTime(String(run.last_auto_resume_attempt_at))}</p>}
                      {Boolean(run.next_retry_at) && <p>Next retry: {formatDateTime(String(run.next_retry_at))}</p>}
                      {Boolean(run.last_auto_resume_error) && <p className="text-danger-display">Last auto error: {String(run.last_auto_resume_error)}</p>}
                    </div>
                    {run.resume_unavailable_reason ? (
                      <p className="mt-2 rounded-md border border-hairline bg-surface px-3 py-2 text-xs text-ink-muted">Resume unavailable: {String(run.resume_unavailable_reason)} {String(run.automatic_resume_note ?? "Automatic resume status unavailable.")}</p>
                    ) : (
                      <p className="mt-2 rounded-md border border-hairline bg-surface px-3 py-2 text-xs text-ink-muted">Manual Resume Now is available as an admin override. {String(run.automatic_resume_note ?? "Due waiting runs normally resume automatically.")}</p>
                    )}
                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      <Button type="button" variant="secondary" disabled={!run.cancel_available || Boolean(actionId)} onClick={() => onCancel(run)}>{actionId === `cancel:${String(run.id)}` ? "Cancelling…" : "Cancel"}</Button>
                      <Button type="button" variant="primary" disabled={!run.manual_resume_available || Boolean(actionId)} onClick={() => onResume(run)}>{actionId === `resume:${String(run.id)}` ? "Resuming…" : run.early_resume_required ? "Resume Early…" : "Resume Now"}</Button>
                    </div>
                  </section>
                )
              })}
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


function FieldRoutesLifecyclePreviewDialog({ open, onOpenChange, loading, data, leadId, onLeadIdChange, subscriptionId, onSubscriptionIdChange, limit, onLimitChange, onLoad }: { open: boolean; onOpenChange: (open: boolean) => void; loading: boolean; data: Record<string, unknown> | null; leadId: string; onLeadIdChange: (value: string) => void; subscriptionId: string; onSubscriptionIdChange: (value: string) => void; limit: string; onLimitChange: (value: string) => void; onLoad: () => void }) {
  const counts = (data?.counts ?? {}) as Record<string, unknown>
  const metadata = (data?.metadata ?? {}) as Record<string, unknown>
  const results = Array.isArray(data?.results) ? data.results as Array<Record<string, unknown>> : []
  const endpoints = Array.isArray(metadata.endpoints) ? metadata.endpoints : []
  const warnings = Array.isArray(metadata.warnings) ? metadata.warnings : []
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,56rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>FieldRoutes Lifecycle Preview</DialogTitle>
          <DialogDescription>Read-only daily lifecycle audit. It proposes LeadFlow and FieldRoutes lifecycle actions without executing them.</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 text-sm">
          <div className="rounded-lg border border-accent/30 bg-accent-faded p-3 text-xs text-ink-body">Preview only — no FieldRoutes writes and no LeadFlow stage changes will occur. The execution gate remains {String(metadata.execution_gate ?? "Disabled")}.</div>
          <div className="grid gap-2 rounded-lg border border-hairline bg-page p-3 md:grid-cols-4 md:items-end">
            <label className="text-xs text-ink-muted">LeadFlow lead ID<Input className="mt-1" value={leadId} onChange={(event) => onLeadIdChange(event.target.value)} placeholder="Exact LeadFlow lead" /></label>
            <label className="text-xs text-ink-muted">FieldRoutes subscription ID<Input className="mt-1" value={subscriptionId} onChange={(event) => onSubscriptionIdChange(event.target.value)} placeholder="Exact subscription" /></label>
            <label className="text-xs text-ink-muted">Batch limit<Input className="mt-1" value={limit} onChange={(event) => onLimitChange(event.target.value)} placeholder="25" /></label>
            <Button type="button" variant="secondary" onClick={onLoad} disabled={loading}>{loading ? "Running preview…" : "Run Preview"}</Button>
          </div>
          {Boolean(data?.error_code) && <div className="rounded-lg border border-danger/30 bg-danger-faded p-3 text-xs text-danger-display">{String(data?.error_message ?? data?.error_code)}</div>}
          {Boolean(metadata.fetched_at) && <div className="rounded-lg border border-hairline bg-page p-3 text-xs text-ink-muted"><p>Fetched at: {String(metadata.fetched_at)}</p><p>Request count: {String(metadata.request_count ?? 0)}</p><p>Timezone: {String(metadata.timezone ?? "America/Chicago")}</p><p>Endpoints: {endpoints.length ? endpoints.map(String).join(", ") : "—"}</p><p>Scope: {JSON.stringify(metadata.search_scope ?? {})}</p></div>}
          {warnings.length > 0 && <div className="rounded-lg border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">{warnings.map((warning, index) => <p key={index}>{String(warning)}</p>)}</div>}
          {loading && <p className="rounded-lg border border-hairline bg-page p-4">Loading FieldRoutes lifecycle preview…</p>}
          {!loading && data && <>
            <div className="grid gap-2 md:grid-cols-4">
              {Object.entries(counts).map(([key, value]) => <div key={key} className="rounded border border-hairline bg-surface p-2"><p className="text-xs text-ink-muted">{humanize(key)}</p><p className="font-medium tabular-nums">{String(value)}</p></div>)}
            </div>
            <div className="space-y-2">
              {results.length > 0 ? results.map((row, index) => {
                const identity = (row.customer_identity ?? {}) as Record<string, unknown>
                const operation = row.proposed_fieldroutes_operation ? String(JSON.stringify(row.proposed_fieldroutes_operation) ?? "None") : "None"
                const category = String(row.category ?? "unknown")
                return <div key={`${String(row.leadflow_lead_id ?? row.fieldroutes_subscription_id ?? index)}-${category}`} className="rounded-lg border border-hairline bg-page p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium">{String(identity.name ?? "LeadFlow lead")}</p><Badge tone={category.includes("review") ? "signal" : category.includes("proposed") ? "accent" : "muted"}>{humanize(category)}</Badge></div>
                  <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2">
                    <span>LeadFlow lead #{String(row.leadflow_lead_id ?? "—")}</span><span>Masked identity: {String(identity.email ?? "—")}</span>
                    <span>FieldRoutes subscription {String(row.fieldroutes_subscription_id ?? "—")}</span><span>FieldRoutes lead {String(row.fieldroutes_lead_id ?? "—")}</span>
                    <span>Current LeadFlow stage: {String(row.current_leadflow_stage ?? "—")}</span><span>Proposed LeadFlow stage: {String(row.proposed_leadflow_stage ?? "—")}</span>
                    <span>active: {String(row.active ?? "—")}</span><span>leadStatus: {String(row.lead_status ?? "—")} {String(row.lead_status_text ?? "")}</span>
                    <span>leadStageID: {String(row.lead_stage_id ?? "—")} {String(row.lead_stage_label ?? "")}</span><span>Stage entered: {String(row.stage_entered_at ?? "—")}</span>
                    <span>Age in current stage: {String(row.age_in_current_stage_days ?? "—")} days</span><span>Alignment category: {humanize(String(row.alignment_category ?? row.category ?? "unknown"))}</span>
                    <span>LeadFlow change needed: {row.leadflow_change_needed ? "Yes" : "No"}</span><span>FieldRoutes change needed: {row.fieldroutes_change_needed ? "Yes" : "No"}</span>
                    <span className="md:col-span-2">Proposed FieldRoutes operation: {operation}</span>
                  </div>
                  <p className="mt-2 text-xs text-ink-body">Reason: {String(row.reason ?? "—")}</p>
                  {Boolean(row.warning) && <p className="mt-2 rounded border border-signal/30 bg-signal-faded px-2 py-1 text-xs text-ink-body">{String(row.warning)}</p>}
                  <p className="mt-1 text-xs text-ink-muted">LeadFlow stage update: {String(row.leadflow_stage_update ?? "Preview only")} · FieldRoutes writes: {String(row.fieldroutes_writes ?? "Disabled")}</p>
                </div>
              }) : <p className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-muted">No tracked LeadFlow leads matched this lifecycle preview scope.</p>}
            </div>
          </>}
        </div>
        <DialogFooter className="shrink-0"><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


function FieldRoutesLeadSyncPreviewDialog({ open, onOpenChange, loading, data, subscriptionId, onSubscriptionIdChange, limit, onLimitChange, onLoad }: { open: boolean; onOpenChange: (open: boolean) => void; loading: boolean; data: Record<string, unknown> | null; subscriptionId: string; onSubscriptionIdChange: (value: string) => void; limit: string; onLimitChange: (value: string) => void; onLoad: () => void }) {
  const counts = (data?.counts ?? {}) as Record<string, unknown>
  const metadata = (data?.metadata ?? {}) as Record<string, unknown>
  const results = Array.isArray(data?.results) ? data.results as Array<Record<string, unknown>> : []
  const endpoints = Array.isArray(metadata.endpoints) ? metadata.endpoints : []
  const warnings = Array.isArray(metadata.warnings) ? metadata.warnings : []
  const downloadPreview = () => {
    if (!data || typeof window === "undefined") return
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `fieldroutes-lead-sync-preview-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,56rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>FieldRoutes Lead Sync Preview</DialogTitle>
          <DialogDescription>Read-only stage comparison for FieldRoutes lead subscriptions. No LeadFlow stages or FieldRoutes records are changed.</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 text-sm">
          <div className="rounded-lg border border-accent/30 bg-accent-faded p-3 text-xs text-ink-body">Preview only — LeadFlow stage updates and FieldRoutes writes are disabled. Outcome status controls Won/Lost; open leads use the configured office stage mapping.</div>
          <div className="grid gap-2 rounded-lg border border-hairline bg-page p-3 md:grid-cols-3 md:items-end">
            <label className="text-xs text-ink-muted">FieldRoutes subscription ID<Input className="mt-1" value={subscriptionId} onChange={(event) => onSubscriptionIdChange(event.target.value)} placeholder="Exact subscription ID" /></label>
            <label className="text-xs text-ink-muted">Recent lead limit<Input className="mt-1" value={limit} onChange={(event) => onLimitChange(event.target.value)} placeholder="25" /></label>
            <Button type="button" variant="secondary" onClick={onLoad} disabled={loading}>{loading ? "Running preview…" : subscriptionId.trim() ? "Preview Subscription" : "Preview Recent Leads"}</Button>
          </div>
          {Boolean(data?.error_code) && <div className="rounded-lg border border-danger/30 bg-danger-faded p-3 text-xs text-danger-display">{String(data?.error_message ?? data?.error_code)}</div>}
          {Boolean(metadata.fetched_at) && <div className="rounded-lg border border-hairline bg-page p-3 text-xs text-ink-muted"><p>Fetched at: {String(metadata.fetched_at)}</p><p>Request count: {String(metadata.request_count ?? 0)}</p><p>Endpoints: {endpoints.length ? endpoints.map(String).join(", ") : "—"}</p><p>Scope: {JSON.stringify(metadata.search_scope ?? {})}</p></div>}
          {warnings.length > 0 && <div className="rounded-lg border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">{warnings.map((warning, index) => <p key={index}>{String(warning)}</p>)}</div>}
          {loading && <p className="rounded-lg border border-hairline bg-page p-4">Loading FieldRoutes lead sync preview…</p>}
          {!loading && data && <>
            <div className="grid gap-2 md:grid-cols-4">
              {Object.entries(counts).map(([key, value]) => <div key={key} className="rounded border border-hairline bg-surface p-2"><p className="text-xs text-ink-muted">{humanize(key)}</p><p className="font-medium tabular-nums">{String(value)}</p></div>)}
            </div>
            <div className="space-y-2">
              {results.length > 0 ? results.map((row, index) => {
                const identity = (row.customer_identity ?? {}) as Record<string, unknown>
                const category = String(row.category ?? "unknown")
                const warning = row.warning ? String(row.warning) : null
                return <div key={`${String(row.fieldroutes_subscription_id ?? index)}-${category}`} className="rounded-lg border border-hairline bg-page p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium">{String(identity.name ?? "FieldRoutes lead")}</p><Badge tone={category.includes("ambiguous") || category.includes("review") ? "signal" : category.includes("change") || category.includes("won") ? "accent" : "muted"}>{humanize(category)}</Badge></div>
                  <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2">
                    <span>Masked identity: {String(identity.email ?? "—")}</span><span>FieldRoutes subscription {String(row.fieldroutes_subscription_id ?? "—")}</span>
                    <span>FieldRoutes lead {String(row.fieldroutes_lead_id ?? "—")}</span><span>FieldRoutes customer {String(row.fieldroutes_customer_id ?? "—")}</span>
                    <span>Active: {String(row.active ?? "—")}</span><span>leadStatus: {String(row.lead_status ?? "—")} {String(row.lead_status_text ?? "")}</span>
                    <span>leadStageID: {String(row.lead_stage_id ?? "—")} {String(row.lead_stage_label ?? "")}</span><span>Match: {String(row.match_status ?? "—")} {String(row.match_method ?? "")}</span>
                    <span>Current LeadFlow stage: {String(row.current_leadflow_stage ?? "—")}</span><span>Proposed LeadFlow stage: {String(row.proposed_leadflow_stage ?? "—")}</span>
                  </div>
                  <p className="mt-2 text-xs text-ink-body">Decision: {String(row.decision_reason ?? "—")}</p>
                  <p className="mt-1 text-xs text-ink-muted">Match reason: {String(row.match_reason ?? "—")}</p>
                  {warning && <p className="mt-2 rounded border border-signal/30 bg-signal-faded px-2 py-1 text-xs text-ink-body">{warning}</p>}
                  <p className="mt-1 text-xs text-ink-muted">LeadFlow stage update: {String(row.leadflow_stage_update ?? "Preview only")} · FieldRoutes writes: {String(row.fieldroutes_writes ?? "Disabled")}</p>
                </div>
              }) : <p className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-muted">No FieldRoutes lead subscriptions matched this preview scope.</p>}
            </div>
          </>}
        </div>
        <DialogFooter className="shrink-0 items-center justify-between gap-2"><div className="text-xs text-ink-muted">FieldRoutes writes: Disabled</div><div className="flex gap-2"><Button type="button" variant="secondary" onClick={downloadPreview} disabled={!data}>Download JSON</Button><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></div></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


function FieldRoutesReconciliationDialog({ open, onOpenChange, loading, data, query, onQueryChange, mode, onModeChange, fieldRoutesCustomerId, onFieldRoutesCustomerIdChange, fieldRoutesSubscriptionId, onFieldRoutesSubscriptionIdChange, onLoad }: { open: boolean; onOpenChange: (open: boolean) => void; loading: boolean; data: Record<string, unknown> | null; query: string; onQueryChange: (value: string) => void; mode: "local" | "live"; onModeChange: (value: "local" | "live") => void; fieldRoutesCustomerId: string; onFieldRoutesCustomerIdChange: (value: string) => void; fieldRoutesSubscriptionId: string; onFieldRoutesSubscriptionIdChange: (value: string) => void; onLoad: (page: number) => void }) {
  const config = (data?.fieldroutes_configuration ?? {}) as Record<string, unknown>
  const counts = (data?.counts ?? {}) as Record<string, unknown>
  const pagination = (data?.pagination ?? {}) as Record<string, unknown>
  const metadata = (data?.metadata ?? {}) as Record<string, unknown>
  const results = Array.isArray(data?.results) ? data.results as Array<Record<string, unknown>> : []
  const page = Number(pagination.page ?? 1)
  const totalPages = Number(pagination.total_pages ?? 1)
  const endpoints = Array.isArray(metadata.endpoints) ? metadata.endpoints : []
  const warnings = Array.isArray(metadata.truncation_warnings) ? metadata.truncation_warnings : []
  const liveScopeReady = fieldRoutesCustomerId.trim().length > 0 || fieldRoutesSubscriptionId.trim().length > 0
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,56rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0"><DialogTitle>FieldRoutes Reconciliation</DialogTitle><DialogDescription>Read-only preview. Live mode queries FieldRoutes; local mode audits stored LeadFlow FieldRoutes context.</DialogDescription></DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 text-sm">
          <div className="rounded-lg border border-accent/30 bg-accent-faded p-3 text-xs text-ink-body">Preview only — no LeadFlow or FieldRoutes records will be changed. Automatic FieldRoutes sync and FieldRoutes writes remain disabled.</div>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-lg border border-hairline bg-page p-3"><p className="text-xs text-ink-muted">Mode</p><p className="font-medium">{String(data?.mode_label ?? (mode === "live" ? "Live FieldRoutes comparison" : "Local stored-data audit"))}</p></div>
            <div className="rounded-lg border border-hairline bg-page p-3"><p className="text-xs text-ink-muted">Configuration</p><p className="font-medium">{String(config.status_label ?? "Not configured")}</p></div>
            <div className="rounded-lg border border-hairline bg-page p-3"><p className="text-xs text-ink-muted">API records</p><p className="font-medium">{String(metadata.api_records_fetched ?? counts.api_records_fetched ?? 0)}</p></div>
            <div className="rounded-lg border border-hairline bg-page p-3"><p className="text-xs text-ink-muted">External writes</p><p className="font-medium">{String(data?.external_writes ?? "Disabled")}</p></div>
          </div>
          <div className="rounded-lg border border-hairline bg-page p-3">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant={mode === "live" ? "primary" : "secondary"} onClick={() => onModeChange("live")}>Live FieldRoutes comparison</Button>
              <Button type="button" variant={mode === "local" ? "primary" : "secondary"} onClick={() => onModeChange("local")}>Local stored-data audit</Button>
            </div>
            {mode === "local" ? <p className="mt-2 rounded-md border border-signal/30 bg-signal-faded px-3 py-2 text-xs text-ink-body">This audit does not query FieldRoutes and may reflect stale stored data.</p> : <p className="mt-2 rounded-md border border-accent/30 bg-accent-faded px-3 py-2 text-xs text-ink-body">Live mode requires a bounded scope such as an exact FieldRoutes customer ID or subscription ID.</p>}
          </div>
          <div className="grid gap-2 rounded-lg border border-hairline bg-page p-3 md:grid-cols-3 md:items-end">
            {mode === "live" ? <>
              <label className="text-xs text-ink-muted">FieldRoutes customer ID<Input className="mt-1" value={fieldRoutesCustomerId} onChange={(event) => onFieldRoutesCustomerIdChange(event.target.value)} placeholder="Exact customer ID" /></label>
              <label className="text-xs text-ink-muted">FieldRoutes subscription ID<Input className="mt-1" value={fieldRoutesSubscriptionId} onChange={(event) => onFieldRoutesSubscriptionIdChange(event.target.value)} placeholder="Exact subscription ID" /></label>
              <Button type="button" variant="secondary" onClick={() => onLoad(1)} disabled={loading || !liveScopeReady}>{loading ? "Running live preview…" : "Run Live Preview"}</Button>
            </> : <>
              <label className="min-w-0 md:col-span-2 text-xs text-ink-muted">Search stored customer, lead, phone, email, or FieldRoutes ID<Input className="mt-1" value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder="Search local audit scope" /></label>
              <Button type="button" variant="secondary" onClick={() => onLoad(1)} disabled={loading}>{loading ? "Auditing…" : "Run Local Audit"}</Button>
            </>}
          </div>
          {Boolean(metadata.fetched_at) && <div className="rounded-lg border border-hairline bg-page p-3 text-xs text-ink-muted"><p>Fetched at: {String(metadata.fetched_at)}</p><p>Request count: {String(metadata.request_count ?? 0)}</p><p>Office scope: {String(metadata.office_scope ?? "—")}</p><p>Endpoints: {endpoints.length ? endpoints.map(String).join(", ") : "—"}</p></div>}
          {warnings.length > 0 && <div className="rounded-lg border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">{warnings.map((warning, index) => <p key={index}>{String(warning)}</p>)}</div>}
          {loading && <p className="rounded-lg border border-hairline bg-page p-4">Loading reconciliation preview…</p>}
          {!loading && data && <>
            <div className="grid gap-2 md:grid-cols-4">
              {Object.entries(counts).filter(([key]) => key !== "records_examined").map(([key, value]) => <div key={key} className="rounded border border-hairline bg-surface p-2"><p className="text-xs text-ink-muted">{humanize(key)}</p><p className="font-medium tabular-nums">{String(value)}</p></div>)}
            </div>
            <div className="space-y-2">
              {results.length > 0 ? results.map((row, index) => {
                const differences = Array.isArray(row.differences) ? row.differences as Array<Record<string, unknown>> : []
                const category = String(row.category ?? "unknown")
                return <div key={`${String(row.leadflow_lead_id ?? "remote")}-${String(row.fieldroutes_subscription_id ?? row.fieldroutes_customer_id ?? index)}-${category}`} className="rounded-lg border border-hairline bg-page p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium">{String(row.customer_name ?? "Unknown customer")}</p><Badge tone={category === "exact_match" ? "accent" : category.includes("mismatch") || category.includes("ambiguous") ? "signal" : "muted"}>{humanize(category)}</Badge></div>
                  <div className="mt-2 grid gap-1 text-xs text-ink-muted md:grid-cols-2">
                    <span>LeadFlow lead #{String(row.leadflow_lead_id ?? "—")}</span><span>FieldRoutes customer {String(row.fieldroutes_customer_id ?? "—")}</span>
                    <span>FieldRoutes lead {String(row.fieldroutes_lead_id ?? "—")}</span><span>Subscription {String(row.fieldroutes_subscription_id ?? "—")}</span>
                    <span>LeadFlow stage: {String(row.leadflow_stage ?? "—")}</span><span>Mapped stage: {String(row.proposed_stage ?? "—")}</span>
                  </div>
                  <p className="mt-2 text-xs text-ink-body">{String(row.reason ?? row.mapping_reason ?? "No reason provided.")}</p>
                  {differences.length > 0 && <div className="mt-2 rounded border border-hairline bg-surface p-2 text-xs"><p className="font-medium text-ink-display">Differences</p>{differences.map((difference, differenceIndex) => <p key={differenceIndex} className="mt-1 text-ink-muted">{humanize(String(difference.field ?? "field"))}: FieldRoutes “{String(difference.fieldroutes_value ?? "—")}” vs LeadFlow “{String(difference.leadflow_value ?? "—")}”</p>)}</div>}
                  <p className="mt-1 text-xs text-ink-muted">Recommended action: {String(row.recommended_action ?? "Review.")}</p>
                </div>
              }) : <p className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-muted">No records matched this reconciliation scope.</p>}
            </div>
          </>}
        </div>
        <DialogFooter className="shrink-0 items-center justify-between gap-2"><div className="text-xs text-ink-muted">Page {page} of {totalPages}</div><div className="flex gap-2"><Button type="button" variant="secondary" disabled={loading || page <= 1 || mode === "live"} onClick={() => onLoad(page - 1)}>Previous</Button><Button type="button" variant="secondary" disabled={loading || page >= totalPages || mode === "live"} onClick={() => onLoad(page + 1)}>Next</Button><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></div></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SystemReadinessDialog({ open, onOpenChange, loading, readiness }: { open: boolean; onOpenChange: (open: boolean) => void; loading: boolean; readiness: Record<string, unknown> | null }) {
  const global = (readiness?.global_runtime ?? {}) as Record<string, unknown>
  const workflow = (readiness?.current_workflow ?? {}) as Record<string, unknown>
  const pipeline = (readiness?.pipeline ?? {}) as Record<string, unknown>
  const integrations = Array.isArray(readiness?.integrations) ? readiness.integrations as Array<Record<string, unknown>> : []
  const support = (readiness?.support_matrix ?? {}) as Record<string, Array<Record<string, unknown>>>
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,56rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0"><DialogTitle>System Readiness</DialogTitle><DialogDescription>Read-only runtime, workflow, pipeline, integration, and block support status.</DialogDescription></DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 text-sm">
          {loading && <p className="rounded-lg border border-hairline bg-page p-4">Loading readiness…</p>}
          {!loading && readiness && <>
            <section className="rounded-lg border border-hairline bg-page p-4"><h3 className="font-medium text-ink-display">Global runtime</h3><div className="mt-2 grid gap-1 md:grid-cols-2"><p>Automatic workflow starts: {String(global.automatic_workflow_execution)}</p><p>Automatic waiting-run resume: {String(global.automatic_waiting_run_resume)}</p><p>Waiting resume gate source: {String(global.waiting_resume_gate_source ?? "—")}</p><p>Recurring job configured: {String(global.waiting_resume_recurring_job_configured ?? "—")}</p><p>Solid Queue supervisor expected: {String(global.waiting_resume_solid_queue_supervisor_expected ?? "—")}</p><p>Due waiting runs: {String(global.due_waiting_count ?? 0)}</p><p>Overdue waiting runs: {String(global.overdue_waiting_count ?? 0)}</p><p>Manual scheduled runs: {String(global.manual_scheduled_runs)}</p><p>Manual internal tests: {String(global.manual_internal_tests)}</p><p>External actions: {String(global.external_workflow_actions)}</p><p>Global gate: {String(global.global_automation_gate)}</p><p>Environment: {String(global.environment)}</p><p>Maximum leads/run: {String(global.application_maximum_leads_per_run)}</p><p>Default leads/run: {String(global.default_leads_per_run)}</p></div></section>
            <section className="rounded-lg border border-hairline bg-page p-4"><h3 className="font-medium text-ink-display">Current workflow readiness</h3><div className="mt-2 grid gap-1 md:grid-cols-2"><p>Name: {String(workflow.workflow_name)}</p><p>ID: {String(workflow.workflow_id)}</p><p>Draft version: {String(workflow.draft_version)}</p><p>Published version: {String(workflow.published_version ?? "—")}</p><p>Automation: {String(workflow.automation_state)}</p><p>Validation: {workflow.validation_valid ? "Valid" : "Has issues"}</p><p>Max leads/run: {String(workflow.maximum_leads_per_run)}</p><p>Recent failures: {String(workflow.recent_failure_count)}</p></div>{Array.isArray(workflow.validation_errors) && workflow.validation_errors.length > 0 && <ul className="mt-2 list-disc pl-5 text-danger-display">{workflow.validation_errors.map((error) => <li key={String(error)}>{String(error)}</li>)}</ul>}</section>
            <section className="rounded-lg border border-hairline bg-page p-4"><h3 className="font-medium text-ink-display">Pipeline readiness</h3><p className="mt-1">Expected stages: New Lead, Needs Follow-Up, Won, Lost</p><p>Status: {pipeline.valid ? "Canonical" : "Warnings found"}</p><ReadinessList title="Missing" items={pipeline.missing} /><ReadinessList title="Unexpected active stages" items={pipeline.unexpected_active_stages} /><ReadinessList title="Duplicates" items={pipeline.duplicates} /></section>
            <section className="rounded-lg border border-hairline bg-page p-4"><h3 className="font-medium text-ink-display">Integration readiness</h3><div className="mt-2 grid gap-2 md:grid-cols-2">{integrations.map((item) => <div key={String(item.name)} className="rounded border border-hairline bg-surface p-2"><p className="font-medium">{String(item.name)}</p><p>{String(item.status)}</p><p className="text-xs text-ink-muted">{String(item.message)}</p></div>)}</div></section>
            <section className="rounded-lg border border-hairline bg-page p-4"><h3 className="font-medium text-ink-display">Runtime support matrix</h3>{Object.entries(support).map(([category, blocks]) => <div key={category} className="mt-3"><p className="font-medium">{humanize(category)}</p><div className="mt-1 grid gap-1">{blocks.map((block) => <div key={String(block.type)} className="rounded border border-hairline bg-surface p-2 text-xs"><span className="font-medium">{String(block.display_label)}</span> · {String(block.current_availability)} · automatic: {String(block.automatic_execution_supported)}</div>)}</div></div>)}</section>
          </>}
        </div>
        <DialogFooter><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReadinessList({ title, items }: { title: string; items: unknown }) {
  const rows = Array.isArray(items) ? items : []
  if (rows.length === 0) return <p className="mt-1 text-xs text-ink-muted">{title}: none</p>
  return <div className="mt-2"><p className="text-xs font-medium">{title}</p><ul className="list-disc pl-5 text-xs text-signal-display">{rows.map((item, index) => <li key={index}>{JSON.stringify(item)}</li>)}</ul></div>
}

function WorkflowValidationDialog({ open, onOpenChange, validation, blockMap, onSelectNode }: { open: boolean; onOpenChange: (open: boolean) => void; validation: WorkflowValidationResult; blockMap: Map<string, WorkflowBlockDefinition>; onSelectNode: (nodeId: string) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[min(90dvh,54rem)] max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Workflow Validation</DialogTitle>
          <DialogDescription>{validation.summary}</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
          <StructuredValidationPanel validation={validation} blockMap={blockMap} onSelectNode={onSelectNode} />
        </div>
        <DialogFooter><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ToastStack({ toasts, onDismiss }: { toasts: WorkflowToast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed right-4 top-4 z-[70] grid w-[min(24rem,calc(100vw-2rem))] gap-2">
      {toasts.map((toast) => (
        <div key={toast.id} className={cn("rounded-lg border p-3 text-sm shadow-lg", toast.tone === "success" ? "border-accent bg-surface text-accent-display" : toast.tone === "error" ? "border-danger bg-surface text-danger-display" : "border-hairline bg-surface text-ink-body")}>
          <div className="flex items-start justify-between gap-3">
            <p>{toast.message}</p>
            <button type="button" className="text-xs text-ink-muted hover:text-ink-display" onClick={() => onDismiss(toast.id)}>Dismiss</button>
          </div>
        </div>
      ))}
    </div>
  )
}

function InternalTestConfirmationDialog({ open, workflow, lead, graph, blockMap, running, onOpenChange, onConfirm }: { open: boolean; workflow: WorkflowData; lead: WorkflowLeadSearchResult | null; graph: WorkflowGraph | null; blockMap: Map<string, WorkflowBlockDefinition>; running: boolean; onOpenChange: (open: boolean) => void; onConfirm: () => void }) {
  const plannedActions = React.useMemo(() => {
    const nodes = graph?.nodes ?? []
    return nodes
      .map((node) => blockMap.get(node.type))
      .filter((block): block is WorkflowBlockDefinition => Boolean(block && block.category === "action" && block.runtime_available && !isExternalWorkflowBlock(block.type)))
  }, [graph, blockMap])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="flex max-h-[90dvh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Confirm Internal Test</DialogTitle>
          <DialogDescription>This test may change the selected lead by moving its stage or creating tasks, follow-ups, and internal notes.</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1 text-sm">
          <div className="rounded-lg border border-hairline bg-page p-3 text-xs">
            <p>Workflow: {workflow.name}</p>
            <p>Graph source: Current editor graph</p>
            <p>Lead: {lead?.customer_name || "Unnamed customer"}</p>
            <p>LeadFlow ID: {lead ? `#${lead.id}` : "—"}</p>
            <p>Customer ID: {lead?.fieldroutes_customer_id || "—"}</p>
          </div>
          <div className="rounded-lg border border-signal/30 bg-signal-faded p-3 text-xs text-ink-body">No webhooks, email, SMS, CTM calls, FieldRoutes calls, broad workflow starts, or unattended schedules will run.</div>
          <div className="rounded-lg border border-hairline bg-page p-3 text-xs">
            <p className="font-medium text-ink-display">Planned internal actions</p>
            {plannedActions.length > 0 ? (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-muted">
                {plannedActions.map((block, index) => <li key={`${block.type}-${index}`}>{block.display_name}</li>)}
              </ul>
            ) : (
              <p className="mt-2 text-ink-muted">No supported internal actions were detected in the current graph.</p>
            )}
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" disabled={running} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" disabled={running || !lead} aria-busy={running} onClick={onConfirm}>{running ? "Running internal test…" : "Confirm Internal Test"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LeadPickerDialog({ open, mode, query, results, message, onOpenChange, onQueryChange, onSearch, onSelect }: { open: boolean; mode: WorkflowRuntimeMode | null; query: string; results: WorkflowLeadSearchResult[]; message: string | null; onOpenChange: (open: boolean) => void; onQueryChange: (value: string) => void; onSearch: () => void; onSelect: (lead: WorkflowLeadSearchResult) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[calc(100dvh-2rem)] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{mode === "internal_test" ? "Select lead for internal test" : "Select lead for dry run"}</DialogTitle>
          <DialogDescription>Search by customer, phone, LeadFlow ID, FieldRoutes customer ID, lead ID, or subscription ID. The selected result submits the internal LeadFlow lead ID.</DialogDescription>
        </DialogHeader>
        <div className="grid min-h-0 gap-3">
          <div className="flex gap-2">
            <Input value={query} onChange={(event) => onQueryChange(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") onSearch() }} placeholder="Search leads" autoFocus />
            <Button type="button" variant="secondary" onClick={onSearch}>Search</Button>
          </div>
          {message && <p className="text-xs text-ink-muted">{message}</p>}
          <div className="workflow-block-picker-scrollbar max-h-[50vh] overflow-y-auto pr-1">
            {results.map((lead) => (
              <button key={lead.id} type="button" className="mb-2 w-full rounded-lg border border-hairline bg-page p-3 text-left text-sm hover:bg-surface" onClick={() => onSelect(lead)}>
                <div className="font-medium text-ink-display">{lead.customer_name || "Unnamed customer"}</div>
                <div className="mt-1 grid gap-1 text-xs text-ink-muted md:grid-cols-2">
                  <span>{lead.phone || "No phone"}</span>
                  <span>LeadFlow #{lead.id}</span>
                  <span>FieldRoutes Customer #{lead.fieldroutes_customer_id || "—"}</span>
                  <span>FieldRoutes Lead #{lead.fieldroutes_lead_id || "—"}</span>
                  <span>FieldRoutes Sub #{lead.fieldroutes_subscription_id || "—"}</span>
                  <span>Stage: {lead.stage_name || lead.stage_key || "Unknown"}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter><Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function WorkflowSidebar({ workflows, activeWorkflowId, sidebarOpen, onToggleSidebar, onCreateWorkflow }: { workflows: WorkflowData[]; activeWorkflowId: number | null; sidebarOpen: boolean; onToggleSidebar: () => void; onCreateWorkflow: () => void }) {
  return (
    <aside className={cn("workflow-builder-sidebar flex min-h-0 shrink-0 flex-col border-r border-hairline bg-surface transition-[width] duration-200", sidebarOpen ? "w-80" : "w-16") }>
      <div className={cn("flex shrink-0 items-center gap-2 border-b border-hairline px-3 py-3", sidebarOpen ? "justify-between" : "justify-center") }>
        {sidebarOpen ? (
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Workflow rail</p>
            <h2 className="text-base">Workflows</h2>
          </div>
        ) : (
          <WorkflowIcon className="h-5 w-5 text-ink-muted" />
        )}
        <Button type="button" variant="secondary" size="sm" onClick={onToggleSidebar} aria-label={sidebarOpen ? "Collapse workflow sidebar" : "Expand workflow sidebar"}>
          {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex shrink-0 flex-col gap-2 border-b border-hairline p-3">
        <Button type="button" variant="secondary" size="sm" onClick={onCreateWorkflow} className={cn("justify-center", !sidebarOpen && "h-9 w-9 px-0") } aria-label="Create workflow">
          <Plus className="h-4 w-4" />
          {sidebarOpen && <span>New workflow</span>}
        </Button>
        {sidebarOpen && <p className="text-xs text-ink-muted">Open a workflow to edit its graph. The sidebar stays separate from the canvas.</p>}
      </div>

      <div className="workflow-scrollbar workflow-scrollbar--surface min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {workflows.length === 0 ? (
          <div className={cn("rounded-lg border border-dashed border-hairline bg-page p-3 text-sm text-ink-muted", !sidebarOpen && "text-center") }>
            {sidebarOpen ? "No workflows yet. Create the first one to start." : "No workflows"}
          </div>
        ) : workflows.map((item) => {
          const active = item.id === activeWorkflowId
          return (
            <Link
              key={item.id}
              href={`/workflows/${item.id}/builder`}
              className={cn(
                "flex items-start gap-3 rounded-lg border px-3 py-3 no-underline transition-colors",
                active ? "border-accent bg-accent-faded text-accent-display" : "border-hairline bg-page text-ink-body hover:bg-surface",
                !sidebarOpen && "justify-center px-0"
              )}
              title={!sidebarOpen ? item.name : undefined}
            >
              <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-sm font-semibold", active ? "border-accent text-accent-display" : "border-hairline bg-surface text-ink-display")}>
                {workflowIconFor(item.icon, "h-4 w-4")}
              </div>
              {sidebarOpen && (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-ink-display">{item.name}</span>
                    <Badge tone={item.enabled ? "accent" : "muted"}>{item.enabled ? "Enabled" : "Disabled"}</Badge>
                  </div>
                  {item.description && <p className="mt-1 line-clamp-2 text-xs text-ink-muted">{item.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-ink-muted">
                    <Badge tone="muted">{humanize(item.status)}</Badge>
                    <Badge tone="muted">{item.execution_mode_label}</Badge>
                    <Badge tone={item.graph_draft_different_from_published ? "signal" : "muted"}>{item.graph_draft_different_from_published ? "Draft changes" : item.graph_state_label}</Badge>
                  </div>
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </aside>
  )
}

function InspectorPanel({ activeWorkflow, selectedNode, selectedBlock, validation, editorReady, inspectorOpen, onToggle, onUpdateSelectedConfig, onDeleteSelected, onDuplicateSelected, stageOptions, users, emailTemplateCatalog, emailTemplateCatalogLoading, onRefreshEmailTemplates }: {
  activeWorkflow: WorkflowData | null
  selectedNode: WorkflowGraphNode | null
  selectedBlock: WorkflowBlockDefinition | null
  validation: WorkflowValidationResult | null
  editorReady: boolean
  inspectorOpen: boolean
  onToggle: () => void
  onUpdateSelectedConfig: (field: WorkflowBlockField, value: string | number | boolean | Record<string, unknown> | unknown[]) => Promise<void>
  onDeleteSelected: () => Promise<void>
  onDuplicateSelected: () => Promise<void>
  stageOptions: Array<{ key: string; name: string; position: number; color: string }>
  users: Array<{ id: number; email: string }>
  emailTemplateCatalog: Record<string, unknown> | null
  emailTemplateCatalogLoading: boolean
  onRefreshEmailTemplates: () => void
}) {
  const visibleConfigFields = selectedBlock ? selectedBlock.config_schema.filter((field) => workflowFieldVisible(field, selectedNode?.config ?? {})) : []

  return (
    <aside className={cn("hidden min-h-0 flex-col border-l border-hairline bg-surface xl:flex", inspectorOpen ? "w-80" : "w-14") }>
      <div className={cn("flex shrink-0 items-center gap-2 border-b border-hairline px-3 py-3", inspectorOpen ? "justify-between" : "justify-center") }>
        {inspectorOpen ? <h2 className="text-base">Inspector</h2> : <Shapes className="h-5 w-5 text-ink-muted" />}
        <Button type="button" variant="secondary" size="sm" onClick={onToggle} aria-label={inspectorOpen ? "Collapse inspector" : "Expand inspector"}>
          {inspectorOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>
      {inspectorOpen && (
        <div className="workflow-scrollbar workflow-scrollbar--surface min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
          {selectedNode && selectedBlock ? (
            <>
              <div className="rounded-lg border border-hairline bg-page p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={selectedBlock.category === "action" && !selectedBlock.runtime_available ? "signal" : "accent"}>{humanize(selectedBlock.category)}</Badge>
                  <Badge tone={selectedBlock.runtime_available ? "accent" : "signal"}>{selectedBlock.runtime_available ? "Runtime ready" : "Runtime later"}</Badge>
                </div>
                <h3 className="mt-2 text-lg">{selectedBlock.display_name}</h3>
                <p className="mt-1 text-sm text-ink-muted">{selectedBlock.description}</p>
                <p className="mt-2 text-xs text-ink-muted">Node ID: {selectedNode.id}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={onDuplicateSelected}>Duplicate</Button>
                  <Button type="button" size="sm" variant="secondary" onClick={onDeleteSelected}>Delete</Button>
                </div>
              </div>
              <div className="space-y-3">
                {visibleConfigFields.length === 0 ? (
                  <p className="text-sm text-ink-muted">This block has no editable config.</p>
                ) : (
                  visibleConfigFields.map((field) => (
                    <FieldEditor key={field.key} nodeId={selectedNode.id} field={field} value={selectedNode.config?.[field.key]} nodeConfig={selectedNode.config ?? {}} stageOptions={stageOptions} users={users} selectedBlock={selectedBlock} onChange={(value) => void onUpdateSelectedConfig(field, value)} />
                  ))
                )}
                {selectedBlock.type === "email.send" && <EmailTemplateCatalogStatus catalog={emailTemplateCatalog} loading={emailTemplateCatalogLoading} onRefresh={onRefreshEmailTemplates} />}
                {selectedBlock.draft_only && <div className="rounded-md border border-signal/40 bg-signal-faded px-3 py-2 text-xs text-ink-body">This block is editor-only for now. Publish will be blocked until a runtime handler exists.</div>}
              </div>
            </>
          ) : (
            <>
              <section className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-body">
                <p className="font-medium text-ink-display">Workflow snapshot</p>
                {activeWorkflow ? (
                  <>
                    <p className="mt-1">{activeWorkflow.trigger_label}</p>
                    <p className="mt-1">{activeWorkflow.execution_mode_label}</p>
                    <p className="mt-1 text-ink-muted">{activeWorkflow.graph_state_label}</p>
                    <p className="mt-1 text-ink-muted">{activeWorkflow.graph_draft_different_from_published ? "Saved draft differs from published graph." : "Draft matches the published graph."}</p>
                  </>
                ) : (
                  <p className="mt-1 text-ink-muted">Create a workflow to start building a graph.</p>
                )}
              </section>
              <section className="rounded-lg border border-hairline bg-page p-4 text-sm text-ink-body">
                <p className="font-medium text-ink-display">Validation</p>
                {validation ? (
                  <div className="mt-2 space-y-2 text-xs text-ink-body">
                    <p>{validation.summary}</p>
                    <p>Errors: {validation.errors.length}</p>
                    <p>Warnings: {validation.warnings.length}</p>
                  </div>
                ) : (
                  <p className="mt-1 text-ink-muted">Run Test workflow to inspect the graph.</p>
                )}
              </section>
              <section className="rounded-lg border border-hairline bg-page p-4 text-xs text-ink-muted">
                <p className="font-medium text-ink-display">Runtime note</p>
                <p className="mt-2">Internal actions only. SMS, email, and outbound webhooks stay reserved for later.</p>
              </section>
            </>
          )}
          {!editorReady && activeWorkflow && <p className="text-xs text-ink-muted">Editor is loading.</p>}
        </div>
      )}
    </aside>
  )
}



function EmailTemplateCatalogStatus({ catalog, loading, onRefresh }: { catalog: Record<string, unknown> | null; loading: boolean; onRefresh: () => void }) {
  const config = (catalog?.configuration ?? {}) as Record<string, unknown>
  const templates = Array.isArray(catalog?.templates) ? catalog.templates as Array<Record<string, unknown>> : []
  return (
    <div className="rounded-md border border-accent/30 bg-accent-faded px-3 py-2 text-xs text-ink-body">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium text-ink-display">Email router template catalog</p>
          <p className="mt-1">Catalog: {String(catalog?.catalog_status ?? "Fallback")} · Templates: {String(catalog?.template_count ?? templates.length)}</p>
          <p className="mt-1">Preview: {String(config.preview_available ? "Available" : "Unavailable")} · Production Send Email: {String(config.production_send_gate ?? "Disabled")}</p>
          <p className="mt-1">Internal Test sends preview_email only to the router’s internal reporting recipient, not to the customer.</p>
          {Boolean(catalog?.stale) && <p className="mt-1 text-signal-display">Template catalog is stale or using fallback metadata.</p>}
        </div>
        <Button type="button" size="sm" variant="secondary" disabled={loading} onClick={onRefresh}>{loading ? "Refreshing…" : "Refresh templates"}</Button>
      </div>
    </div>
  )
}

function EmptyCanvasState({ onCreateWorkflow, workflows }: { onCreateWorkflow: () => void; workflows: WorkflowData[] }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-8">
      <div className="max-w-xl rounded-2xl border border-dashed border-hairline bg-surface p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-faded text-accent-display">
          <WorkflowIcon className="h-6 w-6" />
        </div>
        <h2 className="mt-4 text-2xl">Create your first workflow</h2>
        <p className="mt-2 text-sm text-ink-muted">The canvas is ready. Add a trigger block, connect logic, and keep email/SMS/webhook runtime reserved until later.</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Button type="button" onClick={onCreateWorkflow}><Plus className="h-4 w-4" />Create workflow</Button>
          {workflows.length > 0 && <Badge tone="muted">{workflows.length} existing workflows</Badge>}
        </div>
      </div>
    </div>
  )
}

function BlockPickerDialog({ open, onOpenChange, search, onSearchChange, category, onCategoryChange, blocks, onAddBlock }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  search: string
  onSearchChange: (value: string) => void
  category: BlockCategory
  onCategoryChange: (value: BlockCategory) => void
  blocks: Array<[string, WorkflowBlockDefinition[]]>
  onAddBlock: (type: string) => Promise<void>
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex min-h-0 max-h-[calc(100dvh-2rem)] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add a block</DialogTitle>
          <DialogDescription>Search triggers, logic, and actions. Click a block to add it to the current canvas near the viewport center.</DialogDescription>
        </DialogHeader>
        <div className="workflow-block-picker-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="sticky top-0 z-10 grid gap-3 bg-page pb-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
            <label className="grid gap-2 text-sm">
              Search
              <Input value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Find a block" autoFocus />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {(["all", ...CATEGORY_ORDER] as BlockCategory[]).map((item) => (
                <Button key={item} type="button" size="sm" variant={category === item ? "primary" : "secondary"} onClick={() => onCategoryChange(item)}>
                  {humanize(item)}
                </Button>
              ))}
            </div>
          </div>

          {blocks.length === 0 ? (
            <p className="rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-muted">No blocks match your search.</p>
          ) : (
            blocks.map(([section, items]) => (
              <section key={section} className="space-y-2">
                <h3 className="text-xs uppercase tracking-[0.18em] text-ink-muted">{humanize(section)}</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {items.map((block) => (
                    <button
                      key={block.type}
                      type="button"
                      className={cn("workflow-block-card rounded-lg border border-hairline bg-surface p-3 text-left transition-colors hover:bg-page", blockToneClass(block.category), !block.runtime_available && "border-signal/50")}
                      onClick={() => void onAddBlock(block.type)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-ink-display">{block.display_name}</p>
                          <p className="mt-1 text-xs text-ink-muted">{block.description}</p>
                        </div>
                        <Badge tone={block.runtime_available ? "accent" : "signal"}>{block.runtime_available ? "Ready" : "Reserved"}</Badge>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-ink-muted">
                        <Badge tone="muted">{humanize(block.category)}</Badge>
                        {block.inputs.length > 0 && <Badge tone="muted">{block.inputs.length} input{block.inputs.length === 1 ? "" : "s"}</Badge>}
                        {block.outputs.length > 0 && <Badge tone="muted">{block.outputs.length} output{block.outputs.length === 1 ? "" : "s"}</Badge>}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function isExternalWorkflowBlock(type: string) {
  return ["webhook.call", "webhook.incoming", "email.send", "sms.send"].includes(type)
}

function normalizeWaitUntilConfigForTargetType(config: Record<string, unknown>, targetType: string) {
  const nextConfig: Record<string, unknown> = { ...config, target_type: targetType }
  const clear = (keys: string[]) => keys.forEach((key) => { delete nextConfig[key] })
  if (targetType === "specific_datetime") clear(["weekday", "variable", "datetime"])
  else if (targetType === "next_day_time") clear(["date", "weekday", "variable", "datetime"])
  else if (targetType === "next_weekday_time") clear(["date", "variable", "datetime"])
  else if (targetType === "datetime_variable") clear(["date", "time_of_day", "weekday", "timezone", "datetime"])
  return nextConfig
}

function workflowFieldVisible(field: WorkflowBlockField, config: Record<string, unknown>) {
  const rule = field.visible_when
  if (!rule) return true
  const current = config[rule.field]
  if (Object.prototype.hasOwnProperty.call(rule, "equals")) return current === rule.equals
  if (Object.prototype.hasOwnProperty.call(rule, "not_equals")) return current !== rule.not_equals
  if (Array.isArray(rule.in)) return rule.in.includes(current as string | number | boolean)
  return true
}

function FieldEditor({ nodeId, field, value, nodeConfig, stageOptions, users, selectedBlock, onChange }: { nodeId: string; field: WorkflowBlockField; value: unknown; nodeConfig: Record<string, unknown>; stageOptions: Array<{ key: string; name: string; position: number; color: string }>; users: Array<{ id: number; email: string }>; selectedBlock: WorkflowBlockDefinition | null; onChange: (value: string | number | boolean | Record<string, unknown> | unknown[]) => void }) {
  const isCondition = selectedBlock?.type === "condition"
  const selectedField = String(nodeConfig.field ?? "")
  const selectedVariable = workflowVariableByKey(selectedField)
  const selectedOperator = String(nodeConfig.operator ?? "")
  const valueControl = isCondition && field.key === "value" ? conditionValueControlFor(selectedField, selectedOperator) : field.type
  const displayValue = formatFieldValue(field, value)

  if (isCondition && field.key === "operator") {
    const operatorOptions = operatorsForVariable(selectedField)
    return (
      <FieldShell field={field} extraDescription={selectedVariable ? `Operators are limited to ${selectedVariable.label}.` : "Choose a variable first to see compatible operators."}>
        <Select value={String(displayValue ?? "")} onChange={(event) => onChange(event.target.value)}>
          {operatorOptions.map((operator) => <option key={operator.key} value={operator.key}>{operator.label}</option>)}
        </Select>
      </FieldShell>
    )
  }

  if (isCondition && field.key === "value" && valueControl === "none") {
    return <ConditionExplanation nodeConfig={nodeConfig} />
  }

  if (isCondition && field.key === "value") {
    return (
      <FieldShell field={{ ...field, label: valueControl === "duration" ? "Duration" : "Value" }} extraDescription={selectedVariable?.description}>
        <ValueControl
          control={valueControl}
          value={value}
          stageOptions={stageOptions}
          users={users}
          variableKey={selectedField}
          onChange={onChange}
          identity={`${nodeId}:${field.key}`}
        />
        <ConditionExplanation nodeConfig={nodeConfig} />
      </FieldShell>
    )
  }

  if (field.key === "field" && field.options && field.options.length > 0) {
    return (
      <FieldShell field={field}>
        <Select value={String(displayValue ?? "")} onChange={(event) => onChange(event.target.value)}>
          <option value="">Select a variable</option>
          {field.options.map((option) => <option key={String(option.value)} value={String(option.value)}>{option.label}</option>)}
        </Select>
      </FieldShell>
    )
  }

  const selectedFieldOption = selectedBlock?.config_schema.find((item) => item.key === "field")?.options?.find((option) => option.value === String(nodeConfig.field ?? "")) ?? null
  const selectedFieldKind = selectedFieldOption?.kind ?? null
  const derivedControl = field.key === "value" && selectedFieldKind ? selectedFieldKind : field.type

  return (
    <FieldShell field={field}>
      <ValueControl control={derivedControl} value={value} stageOptions={stageOptions} users={users} variableKey={String(nodeConfig.field ?? "")} options={field.options ?? undefined} identity={`${nodeId}:${field.key}`} onChange={onChange} />
    </FieldShell>
  )
}

function FieldShell({ field, extraDescription, children }: { field: Pick<WorkflowBlockField, "label" | "required" | "description">; extraDescription?: string | null; children: React.ReactNode }) {
  return (
    <div className="grid gap-2 rounded-md border border-hairline bg-page p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span>{field.label}</span>
        {field.required && <Badge tone="signal">Required</Badge>}
      </div>
      {(field.description || extraDescription) && <span className="text-xs text-ink-muted">{field.description || extraDescription}</span>}
      {children}
    </div>
  )
}

function ValueControl({ control, value, stageOptions, users, variableKey, options, identity, onChange }: { control: string; value: unknown; stageOptions: Array<{ key: string; name: string; position: number; color: string }>; users: Array<{ id: number; email: string }>; variableKey?: string; options?: WorkflowBlockField["options"]; identity?: string; onChange: (value: string | number | boolean | Record<string, unknown> | unknown[]) => void }) {
  const variable = workflowVariableByKey(variableKey)
  if (control === "duration") return <DurationControl value={value} onChange={onChange} />
  if (control === "stage_select" || variable?.optionsSource === "pipeline_stages") {
    return <Select value={String(value ?? "")} onChange={(event) => onChange(event.target.value)}><option value="">Select a pipeline stage</option>{stageOptions.map((stage) => <option key={stage.key} value={stage.key}>{stage.name}</option>)}</Select>
  }
  if (control === "user_select" || variable?.optionsSource === "users") {
    return <Select value={String(value ?? "")} onChange={(event) => onChange(event.target.value)}><option value="">Select a user</option>{users.map((user) => <option key={user.id} value={String(user.id)}>{user.email}</option>)}</Select>
  }
  if (control === "boolean") {
    return <Select value={String(value ?? "false")} onChange={(event) => onChange(event.target.value === "true")}><option value="true">Yes</option><option value="false">No</option></Select>
  }
  if (control === "select" || options?.length || variable?.options?.length) {
    const selectOptions = options?.length ? options : variable?.options ?? []
    return <Select value={String(value ?? "")} onChange={(event) => onChange(event.target.value)}><option value="">Select...</option>{selectOptions.map((option) => <option key={String(option.value)} value={String(option.value)}>{option.label}</option>)}</Select>
  }
  if (control === "number") return <Input type="number" value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} />
  if (control === "date") return <Input type="date" value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} />
  if (control === "time") return <Input type="time" value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} />
  if (control === "textarea") return <InspectorTextControl identity={identity ?? control} value={String(value ?? "")} onChange={onChange} multiline className="min-h-28" />
  if (control === "json") return <InspectorTextControl identity={identity ?? control} value={typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2)} onChange={onChange} multiline className="min-h-32 font-mono text-xs" />
  if (control === "datetime") return <Input type="datetime-local" value={formatDateTimeLocalValue(value)} onChange={(event) => onChange(event.target.value)} />
  return <InspectorTextControl identity={identity ?? control} value={String(value ?? "")} onChange={onChange} />
}

function InspectorTextControl({ identity, value, onChange, multiline = false, className }: { identity: string; value: string; onChange: (value: string) => void; multiline?: boolean; className?: string }) {
  const [draftValue, setDraftValue] = React.useState(value)
  const focusedRef = React.useRef(false)
  const identityRef = React.useRef(identity)

  React.useEffect(() => {
    const identityChanged = identityRef.current !== identity
    if (identityChanged) {
      identityRef.current = identity
      setDraftValue(value)
      return
    }
    if (!focusedRef.current && draftValue !== value) {
      setDraftValue(value)
    }
  }, [identity, value, draftValue])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextValue = event.target.value
    setDraftValue(nextValue)
    onChange(nextValue)
  }

  const handleFocus = () => {
    focusedRef.current = true
  }

  const handleBlur = () => {
    focusedRef.current = false
    if (draftValue !== value) setDraftValue(value)
  }

  if (multiline) {
    return <textarea className={cn("form-control form-control-textarea", className)} value={draftValue} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />
  }

  return <Input value={draftValue} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />
}

function DurationControl({ value, onChange }: { value: unknown; onChange: (value: number) => void }) {
  const display = durationDisplayFromMinutes(value)
  const [amount, setAmount] = React.useState(String(display.amount))
  const [unit, setUnit] = React.useState(display.unit)

  React.useEffect(() => {
    const next = durationDisplayFromMinutes(value)
    setAmount(String(next.amount))
    setUnit(next.unit)
  }, [value])

  const update = (nextAmount: string, nextUnit: string) => {
    setAmount(nextAmount)
    setUnit(nextUnit)
    onChange(minutesFromDuration(nextAmount, nextUnit))
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(7rem,auto)] gap-2">
      <Input type="number" min="0" value={amount} onChange={(event) => update(event.target.value, unit)} />
      <Select value={unit} onChange={(event) => update(amount, event.target.value)}>
        {DURATION_UNIT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </div>
  )
}

function ConditionExplanation({ nodeConfig }: { nodeConfig: Record<string, unknown> }) {
  const summary = summarizeConditionConfig(nodeConfig)
  return <p className="rounded-md border border-hairline bg-surface px-3 py-2 text-xs text-ink-muted">{summary}</p>
}

function StructuredValidationPanel({ validation, blockMap, onSelectNode }: { validation: WorkflowValidationResult; blockMap: Map<string, WorkflowBlockDefinition>; onSelectNode: (nodeId: string) => void }) {
  const issues = validation.issues ?? []
  const grouped = issues.reduce((memo, issue) => {
    const key = issue.nodeId || "graph"
    const current = memo.get(key) ?? []
    memo.set(key, [...current, issue])
    return memo
  }, new Map<string, WorkflowValidationIssue[]>())
  return (
    <div className="mt-3 rounded-lg border border-hairline bg-page p-3 text-sm">
      <p className="font-medium text-ink-display">{validation.valid ? "Workflow readiness" : "Cannot publish yet"}</p>
      <div className="mt-3 space-y-3">
        {Array.from(grouped.entries()).map(([nodeId, nodeIssues]) => {
          const first = nodeIssues[0]
          const block = first?.nodeType ? blockMap.get(first.nodeType) : null
          return (
            <section key={nodeId} className="rounded-md border border-hairline bg-surface p-3">
              <button
                type="button"
                className="text-left font-medium text-ink-display disabled:cursor-default"
                disabled={nodeId === "graph"}
                onClick={() => nodeId !== "graph" && onSelectNode(nodeId)}
              >
                {block?.display_name || (nodeId === "graph" ? "Workflow graph" : first?.nodeType || nodeId)}
              </button>
              <ul className="mt-2 space-y-1 text-xs">
                {nodeIssues.map((issue) => (
                  <li key={`${issue.code}-${issue.field ?? ""}-${issue.message}`} className={issue.severity === "error" ? "text-danger-display" : issue.severity === "warning" ? "text-signal-display" : "text-ink-muted"}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            </section>
          )
        })}
      </div>
    </div>
  )
}

function ErrorPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger-display">
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-xs">{body}</p>
    </div>
  )
}

function ErrorBox({ errors }: { errors: string[] }) {
  return (
    <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger-display">
      <p className="font-medium">Please fix the workflow builder:</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
        {errors.map((error) => <li key={error}>{error}</li>)}
      </ul>
    </div>
  )
}


function previewResults(preview: Record<string, unknown> | null) {
  if (!preview) return []
  if (Array.isArray(preview.results)) return preview.results as Array<Record<string, unknown>>
  if (Array.isArray(preview.sample)) return preview.sample as Array<Record<string, unknown>>
  return []
}

function previewStatusLabel(item: Record<string, unknown>) {
  const status = String(item.qualification_status ?? (item.eligible ? "ready" : item.blocked ? "blocked" : "not_qualified"))
  switch (status) {
    case "already_completed": return "Already completed this occurrence"
    case "retryable_failed": return "Retry available"
    case "deferred": return "Deferred by run limit"
    case "not_qualified": return "Not qualified"
    default: return humanize(status)
  }
}

function scheduledRunReadinessMessage(workflow: WorkflowData, preview: Record<string, unknown> | null, staleReason: string | null) {
  if (workflow.publication_status !== "published") return "Publish this workflow before running it across eligible leads."
  if (!workflow.automation_ready) return "Mark this workflow Ready before running it."
  if (!preview?.preflight_id && !preview?.schedule_run_id) return "Preview eligible leads before running this workflow."
  if (staleReason) return staleReason
  if (preview?.expires_at && new Date(String(preview.expires_at)).getTime() <= Date.now()) return "This preview expired. Preview eligible leads again."
  if (Number(preview?.blocked_count ?? 0) > 0) return "Resolve blocked eligible paths before running this workflow."
  if (Number(preview?.will_process_count ?? preview?.ready_count ?? preview?.eligible_count ?? 0) <= 0) return "No ready leads remain for this occurrence. Preview again after the next schedule occurrence."
  return null
}

function publishedScheduleDisplay(workflow: WorkflowData, publishedGraph: WorkflowGraph | null) {
  if (!workflow.published_version) return "No published version"
  const publishedSchedule = scheduleInfoFromGraph(publishedGraph)
  return publishedSchedule?.label ?? "Not configured"
}

function compactScheduleRunSummary(run: WorkflowData["recent_schedule_runs"][number]) {
  const timestamp = formatDateTime(run.started_at) || "Not started"
  if (run.execution_mode === "eligibility_preview") {
    const qualified = Number(run.eligible_count) + Number(run.metadata?.blocked_count ?? 0)
    return `${timestamp} · ${qualified} qualified · No changes made`
  }
  if (run.execution_mode === "dry_run") {
    return `${timestamp} · Dry run · No changes made`
  }
  return `${timestamp} · ${run.completed_count} completed · ${run.failed_count} failed`
}

function plannedActionCounts(preview: Record<string, unknown> | null) {
  const results = previewResults(preview)
  let notes = 0
  let moves = 0
  let tasks = 0
  results.filter((item) => item.will_process !== false && ["ready", "executed", "completed"].includes(String(item.qualification_status ?? "ready"))).forEach((item) => {
    const actions = Array.isArray(item.planned_actions) ? item.planned_actions as Array<Record<string, unknown> | string> : []
    actions.forEach((action) => {
      const text = typeof action === "string" ? action : String(action.node_type ?? action.summary ?? "")
      if (/note/i.test(text)) notes += 1
      else if (/move|stage/i.test(text)) moves += 1
      else if (/task|follow/i.test(text)) tasks += 1
    })
  })
  return { notes, moves, tasks }
}

function groupBlocks(blocks: WorkflowBlockDefinition[], search: string, category: BlockCategory) {
  const needle = search.trim().toLowerCase()
  const groups = new Map<string, WorkflowBlockDefinition[]>()

  blocks
    .filter((block) => category === "all" || block.category === category)
    .filter((block) => !needle || [block.display_name, block.description, block.type, block.category].join(" ").toLowerCase().includes(needle))
    .forEach((block) => {
      const current = groups.get(block.category) ?? []
      groups.set(block.category, [...current, block])
    })

  return CATEGORY_ORDER
    .filter((key) => groups.has(key))
    .map((key) => [key, groups.get(key) ?? []] as [string, WorkflowBlockDefinition[]])
}

function defaultWorkflowForm(triggerTypes: string[]) {
  const triggerType = triggerTypes.includes("lead_created_age") ? "lead_created_age" : triggerTypes[0] ?? "lead_created_age"
  return {
    name: "Untitled workflow",
    description: "",
    icon: "workflow",
    enabled: false,
    trigger_type: triggerType,
    execution_mode: "manual",
    schedule_interval_minutes: "",
    max_leads_per_run: "25",
    conditions_json: JSON.stringify({ stage_keys: ["new_lead"], minimum_minutes: 30, repeat_window_minutes: 60 }, null, 2),
    actions_json: JSON.stringify([{ type: "create_internal_note", body: "Workflow note", category: "internal_alert", pinned: true }], null, 2),
  }
}

function normalizeGraphForEditor(graph: WorkflowGraph, stages: Array<{ key: string; name: string }>): WorkflowGraph {
  const stageKeys = stages.map((stage) => stage.key)
  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({ ...node, config: normalizeNodeConfig(node, stageKeys) })),
    connections: graph.connections.filter((connection) => graph.nodes.some((node) => node.id === connection.sourceNodeId) && graph.nodes.some((node) => node.id === connection.targetNodeId)),
  }
}

function normalizeNodeConfig(node: WorkflowGraphNode, stageKeys: string[]) {
  if (node.type === "condition") return normalizeConditionConfig(node.config ?? {}, stageKeys)
  const next = { ...(node.config ?? {}) }
  if (["schedule.trigger", "task.create", "create_follow_up"].includes(node.type)) {
    const key = node.type === "schedule.trigger" ? "schedule_interval_minutes" : "due_in_minutes"
    if (next[key] !== undefined && next[key] !== null && next[key] !== "") {
      const parsed = Number(next[key])
      next[key] = Number.isFinite(parsed) ? parsed : 0
    }
  }
  if (node.type === "lead.update") {
    const selectedFieldOption = blockValueOptionsForLeadUpdate().find((option) => option.value === String(next.field ?? ""))
    next.value = normalizeValueByKind(next.value, selectedFieldOption?.kind ?? null)
  }
  return next
}

function normalizeFieldValue(field: WorkflowBlockField, value: string | number | boolean | Record<string, unknown> | unknown[], selectedBlock?: WorkflowBlockDefinition | null, nodeConfig: Record<string, unknown> = {}) {
  if (selectedBlock?.type === "condition" && field.key === "value") {
    const condition = normalizeConditionConfig({ ...nodeConfig, value }, [])
    return condition.value
  }
  if (field.type === "duration") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  if (field.key === "value" && selectedBlock?.type === "lead.update") {
    const option = blockValueOptionsForLeadUpdate().find((item) => item.value === String(nodeConfig.field ?? ""))
    return normalizeValueByKind(value, option?.kind ?? null)
  }
  return normalizeValueByKind(value, field.type)
}

function normalizeValueByKind(value: unknown, kind?: string | null) {
  if (kind === "number" || kind === "duration") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  if (kind === "boolean") return value === true || value === "true"
  if (kind === "json") {
    if (typeof value === "string") {
      try { return JSON.parse(value) } catch { return value }
    }
    return value
  }
  return value ?? ""
}

function blockValueOptionsForLeadUpdate() {
  return [
    { value: "lead.stage_key", kind: "stage_select" },
    { value: "lead.assignee_id", kind: "user_select" },
    { value: "lead.potential_value", kind: "number" },
    { value: "lead.notes", kind: "textarea" },
    { value: "lead.attention_needed", kind: "boolean" },
  ]
}

function formatFieldValue(field: WorkflowBlockField, value: unknown) {
  if (field.type === "json") {
    if (typeof value === "string") return value
    return JSON.stringify(value ?? {}, null, 2)
  }
  if (field.type === "boolean") return Boolean(value)
  if (field.type === "number" || field.type === "duration") return value ?? ""
  return value ?? ""
}

function scheduleInfoFromGraph(graph: WorkflowGraph | null | undefined) {
  const node = graph?.nodes?.find((item) => item.type === "schedule.trigger")
  if (!node) return null
  const minutes = Number(node.config?.schedule_interval_minutes ?? 0)
  if (!Number.isFinite(minutes) || minutes <= 0) return null
  const timezone = String(node.config?.timezone || "America/Chicago")
  return { minutes, timezone, label: `Every ${formatDuration(minutes)} · ${timezone}` }
}

function nextOccurrenceLabel(minutes: number) {
  if (!Number.isFinite(minutes) || minutes <= 0) return "Not scheduled"
  const now = new Date()
  const intervalMs = minutes * 60 * 1000
  const next = new Date(Math.ceil(now.getTime() / intervalMs) * intervalMs)
  return formatDateTime(next.toISOString()) || "Not scheduled"
}

function formatDateTime(value: unknown) {
  if (!value) return null
  try {
    return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Chicago" }).format(new Date(String(value)))
  } catch {
    return String(value)
  }
}

function formatDateTimeLocalValue(value: unknown) {
  if (!value) return ""
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return value.slice(0, 16)
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toISOString().slice(0, 16)
}

function summarizeConditionConfig(config: Record<string, unknown>) {
  const normalized = normalizeConditionConfig(config, [])
  const variable = workflowVariableByKey(String(normalized.field ?? ""))
  const operator = workflowOperatorByKey(String(normalized.operator ?? ""))
  const label = variable?.label ?? "Variable"
  const operatorLabel = operator?.label.toLowerCase() ?? String(normalized.operator ?? "matches")
  if (!operatorRequiresValue(String(normalized.operator ?? ""))) return `${label} ${operatorLabel}.`
  const value = formatConditionSummaryValue(normalized.value, variable?.key, variable?.valueControl)
  return `${label} ${operatorLabel} ${value}.`
}

function formatConditionSummaryValue(value: unknown, variableKey?: string, control?: string) {
  if (control === "duration" || variableKey === "lead.current_stage_age_minutes") return formatDuration(value)
  if (variableKey === "lead.stage_key") return humanize(String(value ?? ""))
  if (value === true) return "Yes"
  if (value === false) return "No"
  return String(value ?? "")
}

function formatDuration(value: unknown) {
  const display = durationDisplayFromMinutes(value)
  const unitLabel = display.unit === "days" ? "day" : display.unit === "hours" ? "hour" : "minute"
  return `${display.amount} ${unitLabel}${display.amount === 1 ? "" : "s"}`
}

function readStoredBoolean(key: string, defaultValue: boolean) {
  if (typeof window === "undefined") return defaultValue
  const stored = window.localStorage.getItem(key)
  if (stored === null) return defaultValue
  return stored === "true"
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content ?? ""
}

function workflowIconFor(value: string | null | undefined, className: string) {
  switch (String(value || "workflow")) {
    case "workflow": return <WorkflowIcon className={className} />
    case "zap": return <Zap className={className} />
    case "clock": return <WorkflowIcon className={className} />
    case "calendar": return <CalendarDays className={className} />
    case "message": return <Bell className={className} />
    case "bell": return <Bell className={className} />
    case "pipeline": return <KanbanSquare className={className} />
    default: return <WorkflowIcon className={className} />
  }
}

function humanize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}
